import { useEffect, useMemo, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { fileUrl, checkFileSize } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Save, Trash2, Upload, Sparkles, CheckCircle2, Download, FileText, Image as ImageIcon, Star, Heart, Circle, Square, Diamond, Triangle, Hexagon, X, GripVertical, ChevronDown, Search, Eye, Loader2, ShieldCheck, ClipboardCheck, FolderOpen, AlertTriangle, Plus, Package } from "lucide-react";
import RichText from "@/components/RichText";
import InciAnalysisPanel from "@/components/InciAnalysisPanel";
import { ProductStatus } from "@/components/ProductStatus";
import { UploadLimitNote } from "@/components/UploadLimitNote";
import { useAuth } from "@/lib/auth";
import { useUnsavedChanges, useUnsavedControls } from "@/lib/unsavedChanges";
import { SupplierQuickCreateModal } from "@/components/SupplierQuickCreateModal";
import { PackagingCertificationSection } from "@/components/PackagingCertificationSection";
import { ProductDeleteDialog } from "@/components/ProductDeleteDialog";
import { PictogramImage } from "@/components/PictogramImage";
import { apiErrorMessage } from "@/lib/apiErrorMessage";
import { getActiveToken } from "@/lib/sessionToken";
import { groupPictograms } from "@/lib/pictogramGroups";
import { dedupeSelectablePictograms, isPictogramSelected, togglePictogramSelection } from "@/lib/selectablePictograms";

const TABS = ["anagrafica", "testi", "caratteristiche", "inci", "foto", "barcode", "sicurezza", "logistica", "fornitore", "qr"];
const TAB_LABEL = {
  anagrafica: "Anagrafica", testi: "Testi", caratteristiche: "Caratteristiche",
  inci: "INCI", foto: "Foto", barcode: "Barcode", sicurezza: "Documenti Tecnici",
  logistica: "Logistica", fornitore: "Fornitore", qr: "QR",
};
const RATING_ICONS = {
  star: Star, heart: Heart,
  circle: Circle, square: Square, diamond: Diamond, triangle: Triangle, hexagon: Hexagon,
};
const INCI_STATES = {
  approved: { label: "Approvato", tone: "success" },
  uploaded: { label: "Caricato", tone: "info" },
  needs_review: { label: "Da verificare", tone: "warning" },
  manual: { label: "Inserito manualmente", tone: "warning" },
  draft: { label: "Bozza", tone: "neutral" },
};

const empty = () => ({
  code: "", name: "", technical_name: "", brand_id: "", category_id: "", subcategory_id: "",
  quantity_value: null, udm: "ml",
  description_richtext: "", warnings_richtext: "", usage_richtext: "",
  safety_sheet_current_file_id: null,
  origin_country_code: null, first_eu_market_country_code: null,
  price_value: null, price_currency: "EUR",
  barcode_number: null, barcode_confirmed: false, barcode_file_id: null,
  youtube_url: "", website_url: "",
  pictogram_ids: [], characteristic_values: [],
  weight_g: null, length_cm: null, width_cm: null, height_cm: null,
  qr_enabled: true, is_active: true,
  supplier_id: "", supplier_raw_material_code: "", supplier_product_name: "", supplier_format: "", supplier_order_udm: "",
  has_separate_packaging_supplier: false, packaging_supplier_id: "",
  packaging_supplier_product_code: "", packaging_supplier_product_name: "", packaging_supplier_format: "",
  cpnp_reference: "", cpnp_version: "",
  cpnp_first_notification_date: "", cpnp_last_modification_date: "",
  cpnp_notification_file_id: null, pif_current_file_id: null,
});

export default function ProductEdit() {
  const { canWrite, subscriptionDisabled } = useAuth();
  const { id: routeId } = useParams();
  const nav = useNavigate();
  const [currentId, setCurrentId] = useState(routeId || null);
  const id = currentId;
  const isNew = !id;
  const [tab, setTab] = useState("anagrafica");
  const [data, setData] = useState(empty());
  const [baseline, setBaseline] = useState(() => JSON.stringify(empty()));
  const [loaded, setLoaded] = useState(!routeId);
  const [saving, setSaving] = useState(false);
  const [completion, setCompletion] = useState({ percent: 0, missing: [] });
  const [packagingCount, setPackagingCount] = useState(0);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteImpact, setDeleteImpact] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState("");

  // archives
  const [brands, setBrands] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [cats, setCats] = useState([]);
  const [characteristics, setCharacteristics] = useState([]);
  const [characteristicsLoading, setCharacteristicsLoading] = useState(true);
  const [characteristicsError, setCharacteristicsError] = useState(false);
  const [pictograms, setPictograms] = useState([]);
  const [pictogramCategories, setPictogramCategories] = useState([]);
  const [countries, setCountries] = useState([]);
  const [euCountries, setEuCountries] = useState([]);

  // product-specific
  const [photos, setPhotos] = useState([]);
  const [labels, setLabels] = useState([]);
  const [ingredients, setIngredients] = useState([]);
  const [inciFile, setInciFile] = useState(null);
  const [publicUrl, setPublicUrl] = useState("");
  const [productQr, setProductQr] = useState({ png: null, svg: null, pdf: null });

  const loadCharacteristics = async () => {
    setCharacteristicsLoading(true);
    setCharacteristicsError(false);
    try {
      const response = await api.get("/characteristics");
      setCharacteristics(response.data);
    } catch {
      setCharacteristicsError(true);
    } finally {
      setCharacteristicsLoading(false);
    }
  };

  useEffect(() => {
    Promise.all([
      api.get("/brands").then(r => setBrands(r.data)),
      api.get("/suppliers").then(r => setSuppliers(r.data)).catch(() => setSuppliers([])),
      api.get("/categories").then(r => setCats(r.data)),
      loadCharacteristics(),
      api.get("/pictograms").then(r => setPictograms(r.data)),
      api.get("/pictogram-categories").then(r => setPictogramCategories(r.data)).catch(() => setPictogramCategories([])),
      api.get("/ref/countries").then(r => setCountries(r.data)),
      api.get("/ref/eu-countries").then(r => setEuCountries(r.data)),
    ]).catch(() => {});
  }, []);

  const loadProduct = async (overrideId) => {
    const targetId = overrideId || currentId;
    if (!targetId) return;
    const r = await api.get(`/products/${targetId}`);
    const p = r.data;
    const dObj = {
      code: p.code, name: p.name, technical_name: p.technical_name || "",
      brand_id: p.brand_id || "",
      category_id: p.category_id || "", subcategory_id: p.subcategory_id || "",
      quantity_value: p.quantity_value, udm: p.udm || "ml",
      description_richtext: p.description_richtext || "",
      warnings_richtext: p.warnings_richtext || "",
      usage_richtext: p.usage_richtext || "",
      // IMPORTANT: include this so that a subsequent PUT does NOT wipe it.
      // Uploads via the Sicurezza tab mutate it server-side; without this
      // field in our local state, the next Save would set it back to null.
      safety_sheet_current_file_id: p.safety_sheet_current_file_id || null,
      origin_country_code: p.origin_country_code,
      first_eu_market_country_code: p.first_eu_market_country_code,
      price_value: p.price_value, price_currency: p.price_currency || "EUR",
      barcode_number: p.barcode_number, barcode_confirmed: p.barcode_confirmed,
      barcode_file_id: p.barcode_file_id || null,
      youtube_url: p.youtube_url || "",
      website_url: p.website_url || "",
      pictogram_ids: p.pictogram_ids || [],
      characteristic_values: p.characteristic_values || [],
      weight_g: p.weight_g, length_cm: p.length_cm,
      width_cm: p.width_cm, height_cm: p.height_cm,
      qr_enabled: p.qr_enabled !== false, is_active: p.is_active !== false,
      supplier_id: p.supplier_id || "",
      supplier_raw_material_code: p.supplier_raw_material_code || "",
      supplier_product_name: p.supplier_product_name || "",
      supplier_format: p.supplier_format || "",
      supplier_order_udm: p.supplier_order_udm || "",
      has_separate_packaging_supplier: !!p.has_separate_packaging_supplier,
      packaging_supplier_id: p.packaging_supplier_id || "",
      packaging_supplier_product_code: p.packaging_supplier_product_code || "",
      packaging_supplier_product_name: p.packaging_supplier_product_name || "",
      packaging_supplier_format: p.packaging_supplier_format || "",
      cpnp_reference: p.cpnp_reference || "",
      cpnp_version: p.cpnp_version || "",
      cpnp_first_notification_date: p.cpnp_first_notification_date || "",
      cpnp_last_modification_date: p.cpnp_last_modification_date || "",
      cpnp_notification_file_id: p.cpnp_notification_file_id || null,
      pif_current_file_id: p.pif_current_file_id || null,
    };
    setData(dObj);
    setBaseline(JSON.stringify(dObj));
    setPhotos(p.photos || []);
    setLabels(p.labels || []);
    setIngredients(p.ingredients || []);
    setInciFile(p.inci_file);
    setCompletion(p.completion);
    setPackagingCount(p.packaging_certification_count || 0);
    setPublicUrl(p.public_url);
    setProductQr({ png: p.qr_png_file_id, svg: p.qr_svg_file_id, pdf: p.qr_pdf_file_id });
    setLoaded(true);
  };

  useEffect(() => { if (!isNew) loadProduct(); }, [id]);

  const subcats = useMemo(() => cats.filter(c => c.parent_id === data.category_id), [cats, data.category_id]);
  const topCats = useMemo(() => cats.filter(c => !c.parent_id), [cats]);
  const brand = useMemo(() => brands.find(b => b.id === data.brand_id), [brands, data.brand_id]);
  const RatingIcon = RATING_ICONS[brand?.rating_icon || "star"] || Star;

  const save = async () => {
    if (!data.code?.trim() || !data.name?.trim() || !data.brand_id) {
      toast.error("Codice, nome e brand sono obbligatori");
      return false;
    }
    if (data.supplier_id && !data.supplier_raw_material_code?.trim() && !data.supplier_product_name?.trim()) {
      toast.error("Compila almeno uno tra Codice e Nome prodotto Materia Prima");
      setTab("fornitore");
      return false;
    }
    if (data.has_separate_packaging_supplier && !data.packaging_supplier_id) {
      toast.error("Seleziona il fornitore Packaging");
      setTab("fornitore");
      return false;
    }
    if (data.has_separate_packaging_supplier && data.packaging_supplier_id
        && !data.packaging_supplier_product_code?.trim() && !data.packaging_supplier_product_name?.trim()) {
      toast.error("Compila almeno uno tra Codice e Nome prodotto Packaging");
      setTab("fornitore");
      return false;
    }
    setSaving(true);
    try {
      const payload = { ...data };
      ["quantity_value", "price_value", "weight_g", "length_cm", "width_cm", "height_cm"].forEach(k => {
        if (payload[k] === "" || payload[k] === undefined) payload[k] = null;
        else if (payload[k] !== null) payload[k] = Number(payload[k]);
      });
      let saved;
      if (!currentId) {
        saved = await api.post("/products", payload);
        toast.success("Prodotto creato");
        // Become an existing product without remounting (keeps current tab).
        setCurrentId(saved.data.id);
        window.history.replaceState({}, "", `/products/${saved.data.id}`);
      } else {
        saved = await api.put(`/products/${currentId}`, payload);
        setCompletion(saved.data.completion);
        toast.success("Prodotto salvato");
      }
      // Refresh QR + public URL so other tabs (e.g. QR) reflect changes
      // like a new brand logo without needing a full page reload.
      if (saved?.data) {
        if (saved.data.public_url) setPublicUrl(saved.data.public_url);
        setProductQr({
          png: saved.data.qr_png_file_id,
          svg: saved.data.qr_svg_file_id,
          pdf: saved.data.qr_pdf_file_id,
        });
      }
      setBaseline(JSON.stringify(data));
      return true;
    } catch (e) {
      toast.error(apiErrorMessage(e, "Errore di salvataggio"));
      return false;
    } finally { setSaving(false); }
  };

  // Unsaved-changes guard for the main product form (anagrafica/testi/etc.).
  const mainDirty = canWrite && loaded && baseline !== JSON.stringify(data);
  useUnsavedChanges(mainDirty, save, () => setData(JSON.parse(baseline)));

  // In-page tab-switch guard. Switching product tabs is NOT a route change, so
  // the global route blocker doesn't fire — we gate it here with a dedicated
  // modal ("Salva e Procedi" / "Annulla modifiche" / "Resta qui"). Works for
  // every tab because it consults the global registry (main data + INCI rows).
  const unsaved = useUnsavedControls();
  const [pendingTab, setPendingTab] = useState(null);
  const [tabSaving, setTabSaving] = useState(false);
  const requestTab = (next) => {
    if (next === tab) return;
    if (unsaved?.anyDirty()) setPendingTab(next);
    else setTab(next);
  };
  const tabSaveAndGo = async () => {
    setTabSaving(true);
    try {
      const ok = await unsaved.saveAll();
      if (ok !== false) { setTab(pendingTab); setPendingTab(null); }
    } finally { setTabSaving(false); }
  };
  const tabDiscardAndGo = () => {
    unsaved.discardAll();
    setTab(pendingTab);
    setPendingTab(null);
  };

  /**
   * Ensure the product has been persisted on the backend; auto-creates a new
   * product when called on a "new" page so that the user can upload photos /
   * INCI / barcode / labels without first hitting "Salva". Returns the product
   * id on success, or null if validation failed.
   */
  const ensureSaved = async () => {
    if (!isNew) return id;
    if (!data.code?.trim() || !data.name?.trim() || !data.brand_id) {
      toast.error("Per allegare file servono almeno Codice, Nome e Brand");
      setTab("anagrafica");
      return null;
    }
    setSaving(true);
    try {
      const payload = { ...data };
      ["quantity_value", "price_value", "weight_g", "length_cm", "width_cm", "height_cm"].forEach(k => {
        if (payload[k] === "" || payload[k] === undefined) payload[k] = null;
        else if (payload[k] !== null) payload[k] = Number(payload[k]);
      });
      const saved = await api.post("/products", payload);
      toast.success("Prodotto salvato — pronto per i file");
      setCurrentId(saved.data.id);
      window.history.replaceState({}, "", `/products/${saved.data.id}`);
      return saved.data.id;
    } catch (e) {
      toast.error(apiErrorMessage(e, "Errore di salvataggio"));
      return null;
    } finally { setSaving(false); }
  };

  const openDeleteDialog = async () => {
    setDeleteBusy(true);
    setDeleteError("");
    try {
      const { data: impact } = await api.get(`/products/${id}/deletion-impact`);
      setDeleteImpact(impact);
      setShowDeleteConfirm(true);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Impossibile verificare i prodotti cliente collegati"));
    } finally { setDeleteBusy(false); }
  };

  const del = async () => {
    setDeleteBusy(true);
    setDeleteError("");
    try {
      const { data: result } = await api.delete(`/products/${id}`, {
        params: deleteImpact?.client_product_count ? { force: true } : {},
      });
      setShowDeleteConfirm(false);
      toast.success(result.deleted_client_products
        ? `Prodotto e ${result.deleted_client_products} prodotti cliente eliminati`
        : "Prodotto eliminato");
      nav("/products");
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 409 && detail && typeof detail === "object") {
        setDeleteImpact(current => ({
          ...current,
          client_product_count: detail.client_product_count || detail.clients?.length || 0,
          client_products: Array.isArray(detail.clients) ? detail.clients : [],
        }));
        setDeleteError("Sono stati aggiunti prodotti cliente. Verifica l'elenco e conferma nuovamente.");
      } else {
        setDeleteError(apiErrorMessage(e, "Errore durante l'eliminazione del prodotto"));
      }
    } finally { setDeleteBusy(false); }
  };

  if (!loaded) return <div className="p-10 text-sm text-[#707070]">Caricamento...</div>;

  return (
    <div className="products-ux p-4 pt-16 md:p-8 lg:p-12" data-testid="product-edit">
      <div className="flex items-center justify-between mb-6">
        <button onClick={() => nav("/products")} className="text-xs tracking-luxury uppercase text-[#707070] hover:text-[#1A1A1A] flex items-center" data-testid="back-to-products-btn">
          <ArrowLeft className="w-3 h-3 mr-2" strokeWidth={1.5} />Torna ai prodotti
        </button>
        <div className="flex gap-3">
          {!isNew && canWrite && (
            <button onClick={openDeleteDialog} disabled={deleteBusy}
                    className="btn-ghost flex items-center text-[#8C3A3A] border-[#8C3A3A] disabled:opacity-50" data-testid="delete-product-btn">
              <Trash2 className="w-3 h-3 mr-2" strokeWidth={1.5} />{deleteBusy ? "Verifica..." : "Elimina"}
            </button>
          )}
          {canWrite && <button onClick={save} disabled={saving} className="btn-primary flex items-center" data-testid="save-product-btn">
            <Save className="w-3 h-3 mr-2" strokeWidth={2} />{saving ? "Salvataggio..." : "Salva"}
          </button>}
        </div>
      </div>

      <ProductDeleteDialog
        open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}
        impact={deleteImpact} busy={deleteBusy} error={deleteError} onConfirm={del}
      />

      <div className="mb-8">
        <div className="text-[10px] tracking-luxury text-[#707070] uppercase mb-1">
          {isNew ? "Nuovo Prodotto" : data.code}
        </div>
        <h1 className="font-serif text-3xl md:text-4xl">{data.name || "Senza nome"}</h1>
      </div>

      {!isNew && (
        <div className="luxury-card p-5 mb-6" data-testid="completion-bar">
          <div className="flex justify-between items-center mb-2">
            <div className="text-xs tracking-luxury uppercase">Completezza scheda</div>
            <div className="font-medium" data-testid="product-completeness-value">{completion.percent}%</div>
          </div>
          <div className="h-1 bg-[#E5E5E0] rounded-full overflow-hidden" role="progressbar"
               aria-label="Completezza scheda" aria-valuemin={0} aria-valuemax={100}
               aria-valuenow={completion.percent} data-testid="product-completeness-progress">
            <div className={`h-full transition-[width,background-color] duration-300 ${completion.percent >= 100 ? "product-ux-progress--success" : "product-ux-progress--warning"}`}
                 style={{ width: `${completion.percent}%` }} />
          </div>
          {completion.missing?.length > 0 && (
            <div className="product-ux-warning-text text-xs mt-3" data-testid="product-missing-fields">
              Mancano: {completion.missing.join(", ")}
            </div>
          )}
        </div>
      )}

      <div className="flex border-b border-[#E5E5E0] mb-8 overflow-x-auto">
        {TABS.map(t => (
          <button key={t} onClick={() => requestTab(t)}
                   className={`tab-btn ${tab === t ? "active" : ""}`}
                   data-testid={`tab-${t}`}>
            {TAB_LABEL[t]}
          </button>
        ))}
      </div>

      {pendingTab && (
        <div className="fixed inset-0 bg-black/40 z-[90] flex items-center justify-center px-4"
             data-testid="tab-unsaved-modal">
          <div className="bg-white rounded-sm shadow-xl w-full max-w-md p-6">
            <div className="flex items-start gap-3 mb-3">
              <div className="w-9 h-9 rounded-full bg-[#FBF3E7] flex items-center justify-center shrink-0">
                <AlertTriangle className="w-4 h-4 text-[#A07A2A]" strokeWidth={2} />
              </div>
              <div>
                <h3 className="font-serif text-2xl leading-tight">Modifiche non salvate</h3>
                <p className="text-sm text-[#707070] mt-1">
                  Hai delle modifiche non salvate. Vuoi salvarle prima di procedere?
                </p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row justify-end gap-2 mt-5">
              <button onClick={()=>setPendingTab(null)} disabled={tabSaving}
                      className="btn-ghost order-3 sm:order-1"
                      data-testid="tab-unsaved-stay-btn">
                Resta qui
              </button>
              <button onClick={tabDiscardAndGo} disabled={tabSaving}
                      className="btn-ghost text-[#8C3A3A] order-2"
                      data-testid="tab-unsaved-discard-btn">
                Annulla modifiche
              </button>
              <button onClick={tabSaveAndGo} disabled={tabSaving}
                      className={`btn-primary order-1 sm:order-3 ${tabSaving ? "opacity-50 cursor-not-allowed" : ""}`}
                      data-testid="tab-unsaved-save-btn">
                {tabSaving ? "Salvataggio..." : "Salva e Procedi"}
              </button>
            </div>
          </div>
        </div>
      )}

      {tab === "caratteristiche" && characteristicsError && (
        <div role="alert" className="text-sm text-[#8C3A3A] mb-4" data-testid="product-characteristics-error">
          Impossibile caricare le caratteristiche. I valori del prodotto non sono stati modificati.
          <button type="button" onClick={loadCharacteristics} className="underline ml-2" data-testid="product-characteristics-retry">Riprova</button>
        </div>
      )}
      <fieldset disabled={!canWrite} className="luxury-card p-4 md:p-8 min-w-0">
        {tab === "anagrafica" && <Anagrafica data={data} setData={setData} brands={brands} topCats={topCats} subcats={subcats} countries={countries} euCountries={euCountries} />}
        {tab === "testi" && <Testi data={data} setData={setData} pid={id} isNew={isNew} ensureSaved={ensureSaved} brands={brands} cats={cats} characteristics={characteristics} />}
        {tab === "caratteristiche" && <Caratteristiche data={data} setData={setData} characteristics={characteristics} characteristicsLoading={characteristicsLoading} characteristicsError={characteristicsError} pictograms={pictograms} pictogramCategories={pictogramCategories} RatingIcon={RatingIcon} />}
        {tab === "inci" && <InciTab pid={id} isNew={isNew} ensureSaved={ensureSaved} inciFile={inciFile} setInciFile={setInciFile} ingredients={ingredients} setIngredients={setIngredients} reload={loadProduct} />}
        {tab === "foto" && <FotoTab pid={id} isNew={isNew} ensureSaved={ensureSaved} photos={photos} setPhotos={setPhotos} labels={labels} setLabels={setLabels} reload={loadProduct} />}
        {tab === "barcode" && <BarcodeTab pid={id} isNew={isNew} ensureSaved={ensureSaved} data={data} setData={setData} reload={loadProduct} />}
        {tab === "sicurezza" && (
          <DocumentiTecnici pid={id} isNew={isNew} ensureSaved={ensureSaved}
                            data={data} setData={setData} reload={loadProduct}
                            packagingCount={packagingCount} onPackagingCountChange={setPackagingCount} />
        )}
        {tab === "logistica" && <Logistica data={data} setData={setData} pid={id} isNew={isNew} ensureSaved={ensureSaved} reload={loadProduct} />}
        {tab === "fornitore" && <Fornitore data={data} setData={setData} suppliers={suppliers} setSuppliers={setSuppliers} />}
        {tab === "qr" && <QrTab pid={id} isNew={isNew} publicUrl={publicUrl} productQr={productQr} data={data} setData={setData} />}
      </fieldset>
    </div>
  );
}

function Anagrafica({ data, setData, brands, topCats, subcats, countries, euCountries }) {
  const set = (k, v) => setData(d => ({ ...d, [k]: v }));
  const euCodeSet = useMemo(() => new Set(euCountries.map(c => c.code)), [euCountries]);
  const isOriginEu = !!data.origin_country_code && euCodeSet.has(data.origin_country_code);
  // When the origin is an EU country, clear the redundant "Prima immissione UE" field.
  useEffect(() => {
    if (isOriginEu && data.first_eu_market_country_code) {
      setData(d => ({ ...d, first_eu_market_country_code: null }));
    }
  }, [isOriginEu, data.first_eu_market_country_code, setData]);
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div>
        <label className="label-tiny">Codice prodotto *</label>
        <input className="input-luxury" data-testid="input-code" value={data.code}
               onChange={(e)=>set("code", e.target.value)} />
      </div>
      <div>
        <label className="label-tiny">Nome prodotto *</label>
        <input className="input-luxury" data-testid="input-name" value={data.name}
               onChange={(e)=>set("name", e.target.value)} />
      </div>
      <div className="lg:col-span-2">
        <label className="label-tiny">Nome tecnico prodotto *</label>
        <input className="input-luxury" data-testid="input-technical-name"
                value={data.technical_name || ""}
                placeholder="es. Rossetto liquido matte formula RL-2025"
                onChange={(e)=>set("technical_name", e.target.value)} />
      </div>
      <div>
        <label className="label-tiny">Brand *</label>
        <select className={`input-luxury ${!data.brand_id ? "product-ux-select-empty" : ""}`} data-testid="input-brand" value={data.brand_id}
                onChange={(e)=>set("brand_id", e.target.value)}>
          <option value="">— Seleziona —</option>
          {brands.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
        </select>
      </div>
      <div>
        <label className="label-tiny">Categoria</label>
        <select className={`input-luxury ${!data.category_id ? "product-ux-select-empty" : ""}`} data-testid="input-category" value={data.category_id}
                onChange={(e)=>{ set("category_id", e.target.value); set("subcategory_id", ""); }}>
          <option value="">— Seleziona —</option>
          {topCats.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </div>
      <div>
        <label className="label-tiny">Sottocategoria</label>
        <select className={`input-luxury ${!data.subcategory_id ? "product-ux-select-empty" : ""}`} data-testid="input-subcategory" value={data.subcategory_id || ""}
                onChange={(e)=>set("subcategory_id", e.target.value)}>
          <option value="">— Nessuna —</option>
          {subcats.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </div>
      <div className="grid grid-cols-3 gap-3">
        <div className="col-span-2">
          <label className="label-tiny">Quantità</label>
          <input className="input-luxury" data-testid="input-quantity" type="number" step="0.01"
                 value={data.quantity_value ?? ""}
                 onChange={(e)=>set("quantity_value", e.target.value)} />
        </div>
        <div>
          <label className="label-tiny">UDM</label>
          <select className="input-luxury" data-testid="input-udm" value={data.udm || ""}
                  onChange={(e)=>set("udm", e.target.value)}>
            <option value="ml">ml</option><option value="L">L</option>
            <option value="g">g</option><option value="kg">kg</option>
            <option value="pz">pz</option>
          </select>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-3">
        <div className="col-span-2">
          <label className="label-tiny">Prezzo vendita</label>
          <input className="input-luxury" data-testid="input-price" type="number" step="0.01"
                 value={data.price_value ?? ""}
                 onChange={(e)=>set("price_value", e.target.value)} />
        </div>
        <div>
          <label className="label-tiny">Valuta</label>
          <select className="input-luxury" data-testid="input-currency" value={data.price_currency || "EUR"}
                  onChange={(e)=>set("price_currency", e.target.value)}>
            <option value="EUR">EUR</option><option value="USD">USD</option>
          </select>
        </div>
      </div>
      <div>
        <label className="label-tiny">Origine prodotto</label>
        <CountryPicker
          value={data.origin_country_code || ""}
          options={countries}
          onChange={(code) => set("origin_country_code", code || null)}
          placeholder="— Seleziona —"
          testId="input-origin"
        />
      </div>
      {!isOriginEu && (
        <div data-testid="eu-market-wrapper">
          <label className="label-tiny">Prima immissione UE</label>
          <CountryPicker
            value={data.first_eu_market_country_code || ""}
            options={euCountries}
            onChange={(code) => set("first_eu_market_country_code", code || null)}
            placeholder="— Seleziona —"
            testId="input-eu-market"
          />
        </div>
      )}
      <div className="lg:col-span-2 flex flex-wrap gap-x-6 gap-y-3">
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={data.is_active}
                 data-testid="input-is-active"
                 onChange={(e)=>set("is_active", e.target.checked)} />
          Prodotto attivo
          <ProductStatus tone={data.is_active ? "success" : "neutral"} testId="product-active-status">
            {data.is_active ? "Attivo" : "Non attivo"}
          </ProductStatus>
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={data.qr_enabled}
                 data-testid="input-qr-enabled"
                 onChange={(e)=>set("qr_enabled", e.target.checked)} />
          Pagina QR pubblica attiva
          <ProductStatus tone={data.qr_enabled ? "success" : "neutral"} testId="product-qr-status">
            {data.qr_enabled ? "Attiva" : "Non attiva"}
          </ProductStatus>
        </label>
      </div>
    </div>
  );
}

function Fornitore({ data, setData, suppliers, setSuppliers }) {
  const [quickTarget, setQuickTarget] = useState(null);
  const set = (k, v) => setData(d => ({ ...d, [k]: v }));
  const supplierOptions = (suppliers || []).map(s => (
    <option key={s.id} value={s.id}>{s.company_name || s.name}</option>
  ));
  const supplierCreated = (supplier) => {
    setSuppliers(list => [...list, supplier].sort((a, b) =>
      (a.company_name || a.name || "").localeCompare(b.company_name || b.name || "", "it")
    ));
    set(quickTarget === "packaging" ? "packaging_supplier_id" : "supplier_id", supplier.id);
  };
  return (
    <div className="max-w-4xl space-y-8" data-testid="fornitore-tab">
      <section className="space-y-5" data-testid="raw-material-supplier-block">
        <div>
          <div className="text-[10px] tracking-luxury text-[#707070] uppercase mb-1">Materia Prima</div>
          <div className="text-xs text-[#707070]">Dati del prodotto acquistato dal fornitore della materia prima.</div>
        </div>
        <div>
          <label className="label-tiny">Fornitore Materia Prima</label>
          <div className="flex gap-2">
            <select className={`input-luxury flex-1 ${!data.supplier_id ? "product-ux-select-empty" : ""}`} data-testid="input-supplier"
                    value={data.supplier_id || ""} onChange={event => set("supplier_id", event.target.value)}>
              <option value="">— Nessuno —</option>{supplierOptions}
            </select>
            <button type="button" onClick={() => setQuickTarget("raw_material")}
                    className="w-11 shrink-0 border border-[#D8D2C6] hover:border-[#1A1A1A] inline-flex items-center justify-center transition-colors"
                    title="Crea nuovo fornitore" aria-label="Crea nuovo fornitore Materia Prima"
                    data-testid="raw-material-supplier-add">
              <Plus className="w-4 h-4" />
            </button>
          </div>
        </div>
        {data.supplier_id && (
          <>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <label className="label-tiny">Codice Prodotto Materia Prima</label>
              <input className="input-luxury" data-testid="input-supplier-code"
                     value={data.supplier_raw_material_code || ""}
                     placeholder="es. RM-00123"
                     onChange={(e)=>set("supplier_raw_material_code", e.target.value)} />
            </div>
            <div>
              <label className="label-tiny">Nome Prodotto Materia Prima</label>
              <input className="input-luxury" data-testid="input-supplier-name"
                     value={data.supplier_product_name || ""}
                     placeholder="es. Gel base trasparente"
                     onChange={(e)=>set("supplier_product_name", e.target.value)} />
            </div>
          </div>
          <div className="text-xs text-[#707070] -mt-3">Compila almeno uno tra Codice e Nome prodotto Materia Prima.</div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <label className="label-tiny">Formato</label>
              <input className="input-luxury" data-testid="input-supplier-format"
                     value={data.supplier_format || ""}
                     placeholder="es. Flacone 250 ml, Fusto 25 kg..."
                     onChange={(e)=>set("supplier_format", e.target.value)} />
            </div>
            <div>
              <label className="label-tiny">UDM ordine fornitore</label>
              <select className={`input-luxury ${!data.supplier_order_udm ? "product-ux-select-empty" : ""}`} data-testid="input-supplier-udm"
                      value={data.supplier_order_udm || ""}
                      onChange={(e)=>set("supplier_order_udm", e.target.value)}>
                <option value="">— Seleziona —</option>
                <option value="gr">gr (Grammi)</option>
                <option value="kg">kg (Chilogrammi)</option>
                <option value="mg">mg (Milligrammi)</option>
                <option value="ml">ml (Millilitri)</option>
                <option value="cl">cl (Centilitri)</option>
                <option value="L">L (Litri)</option>
                <option value="pz">pz (Pezzi)</option>
                <option value="ct">ct (Cartoni)</option>
                <option value="conf">conf (Confezioni)</option>
                <option value="gallone">Gallone</option>
              </select>
            </div>
          </div>
          </>
        )}
      </section>

      <div className="border-t border-[#E5E5E0] pt-7">
        <label className="flex items-start gap-3 cursor-pointer" data-testid="packaging-supplier-toggle-label">
          <input type="checkbox" checked={!!data.has_separate_packaging_supplier}
                 onChange={event => set("has_separate_packaging_supplier", event.target.checked)}
                 className="mt-0.5" data-testid="packaging-supplier-toggle" />
          <span>
            <span className="text-sm font-medium text-[#1A1A1A]">Hai un fornitore diverso per il packaging?</span>
            <span className="block text-xs text-[#707070] mt-1">
              Se non selezionato, il packaging coincide con il fornitore della Materia Prima.
            </span>
          </span>
        </label>
      </div>

      {data.has_separate_packaging_supplier && (
        <section className="space-y-5 border-l-2 border-[#C5B39B] pl-5 md:pl-7" data-testid="packaging-supplier-block">
          <div className="text-[10px] tracking-luxury text-[#707070] uppercase">Packaging</div>
          <div>
            <label className="label-tiny">Fornitore Packaging</label>
            <div className="flex gap-2">
              <select className={`input-luxury flex-1 ${!data.packaging_supplier_id ? "product-ux-select-empty" : ""}`} value={data.packaging_supplier_id || ""}
                      onChange={event => set("packaging_supplier_id", event.target.value)}
                      data-testid="input-packaging-supplier">
                <option value="">— Seleziona —</option>{supplierOptions}
              </select>
              <button type="button" onClick={() => setQuickTarget("packaging")}
                      className="w-11 shrink-0 border border-[#D8D2C6] hover:border-[#1A1A1A] inline-flex items-center justify-center transition-colors"
                      title="Crea nuovo fornitore" aria-label="Crea nuovo fornitore Packaging"
                      data-testid="packaging-supplier-add">
                <Plus className="w-4 h-4" />
              </button>
            </div>
          </div>
          {data.packaging_supplier_id && (
            <>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div>
                  <label className="label-tiny">Codice Prodotto Pack</label>
                  <input className="input-luxury" value={data.packaging_supplier_product_code || ""}
                         onChange={event => set("packaging_supplier_product_code", event.target.value)}
                         placeholder="es. PACK-00123" data-testid="input-packaging-supplier-code" />
                </div>
                <div>
                  <label className="label-tiny">Nome Prodotto Pack</label>
                  <input className="input-luxury" value={data.packaging_supplier_product_name || ""}
                         onChange={event => set("packaging_supplier_product_name", event.target.value)}
                         placeholder="es. Flacone vetro 15 ml" data-testid="input-packaging-supplier-name" />
                </div>
              </div>
              <div className="text-xs text-[#707070] -mt-3">Compila almeno uno tra Codice e Nome prodotto Packaging.</div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div>
                <label className="label-tiny">Formato</label>
                <input className="input-luxury" value={data.packaging_supplier_format || ""}
                       onChange={event => set("packaging_supplier_format", event.target.value)}
                       placeholder="es. Scatola da 100 pz" data-testid="input-packaging-supplier-format" />
                </div>
              </div>
            </>
          )}
        </section>
      )}

      <SupplierQuickCreateModal open={!!quickTarget} onClose={() => setQuickTarget(null)} onCreated={supplierCreated} />
    </div>
  );
}

function Testi({ data, setData, pid, isNew, ensureSaved, brands, cats, characteristics }) {
  const [improving, setImproving] = useState(false);
  const set = (k, v) => setData(d => ({ ...d, [k]: v }));

  // Resolve the subcategory and category so we can show inherited content
  // when the product field is empty.
  const subcategory = useMemo(
    () => cats.find(c => c.id === data.subcategory_id),
    [cats, data.subcategory_id]
  );
  const category = useMemo(
    () => cats.find(c => c.id === data.category_id),
    [cats, data.category_id]
  );
  const sourceName = subcategory?.name || category?.name;
  // Fields fall back to the subcategory first, then to the parent category
  // (so a category-level default also covers all its subcategories).
  const inherited = {
    description_richtext: subcategory?.description_richtext || category?.description_richtext || "",
    usage_richtext: subcategory?.usage_richtext || category?.usage_richtext || "",
    warnings_richtext: subcategory?.warnings_richtext || category?.warnings_richtext || "",
  };

  const isEmpty = (html) => !html || html.replace(/<[^>]+>/g, "").trim() === "";

  const improve = async () => {
    const productId = pid || (ensureSaved ? await ensureSaved() : null);
    if (!productId) return;
    setImproving(true);
    try {
      const r = await api.post(`/products/${productId}/ai/improve-description`, { current: data.description_richtext });
      if (r.data.text && confirm("Anteprima AI:\n\n" + r.data.text + "\n\nVuoi sostituire la descrizione attuale?")) {
        set("description_richtext", r.data.text);
        toast.success("Descrizione aggiornata");
      }
    } catch { toast.error("Errore AI"); }
    finally { setImproving(false); }
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="flex justify-between items-center mb-2">
          <label className="label-tiny mb-0">Descrizione</label>
          <button onClick={improve} disabled={improving} className="text-xs flex items-center text-[#1A1A1A] hover:text-[#707070]" data-testid="improve-desc-btn">
            <Sparkles className="w-3 h-3 mr-1" strokeWidth={1.5} />
            {improving ? "Elaborazione AI..." : "Migliora con AI"}
          </button>
        </div>
        <RichText value={data.description_richtext || ""}
                  onChange={(v)=>set("description_richtext", v)}
                  testId="input-description"
                  placeholder="Descrivi il prodotto..." minHeight={160} />
        <InheritedPreview
          field="description_richtext"
          fieldLabel="descrizione"
          productValue={data.description_richtext}
          inheritedValue={inherited.description_richtext}
          sourceName={sourceName}
          isEmpty={isEmpty}
          onCopy={(v) => set("description_richtext", v)}
          testId="inherited-description"
        />
      </div>
      <div>
        <label className="label-tiny">Modo d&apos;uso</label>
        <RichText value={data.usage_richtext || ""}
                  onChange={(v)=>set("usage_richtext", v)}
                  testId="input-usage"
                  placeholder="Come si applica il prodotto..." minHeight={120} />
        <InheritedPreview
          field="usage_richtext"
          fieldLabel="modo d'uso"
          productValue={data.usage_richtext}
          inheritedValue={inherited.usage_richtext}
          sourceName={sourceName}
          isEmpty={isEmpty}
          onCopy={(v) => set("usage_richtext", v)}
          testId="inherited-usage"
        />
      </div>
      <div>
        <label className="label-tiny">Avvertenze</label>
        <RichText value={data.warnings_richtext || ""}
                  onChange={(v)=>set("warnings_richtext", v)}
                  testId="input-warnings"
                  placeholder="Avvertenze, precauzioni..." minHeight={120} />
        <InheritedPreview
          field="warnings_richtext"
          fieldLabel="avvertenze"
          productValue={data.warnings_richtext}
          inheritedValue={inherited.warnings_richtext}
          sourceName={sourceName}
          isEmpty={isEmpty}
          onCopy={(v) => set("warnings_richtext", v)}
          testId="inherited-warnings"
        />
      </div>
      <div>
        <label className="label-tiny">Video YouTube (URL)</label>
        <input className="input-luxury" data-testid="input-youtube"
               value={data.youtube_url || ""}
               onChange={(e)=>set("youtube_url", e.target.value)}
               placeholder="https://www.youtube.com/watch?v=..." />
      </div>
      <div className="border-t border-[#E5E5E0] pt-4 mt-2">
        <label className="label-tiny">Link sito (URL acquisto)</label>
        <input className="input-luxury" data-testid="input-website-url"
               value={data.website_url || ""}
               onChange={(e)=>set("website_url", e.target.value)}
               placeholder="https://www.tuosito.com/prodotto" />
        <div className="text-xs text-[#707070] mt-2">
          La visibilità del bottone <strong>&quot;Acquista subito&quot;</strong> sulla pagina QR pubblica si gestisce dalla pagina <em>Layout QR</em> (blocco &quot;Acquista subito&quot;).
        </div>
      </div>
    </div>
  );
}

/**
 * Renders a small "inherited from subcategory" panel below a text field.
 * - When the field is empty and the subcategory has content → shows the
 *   inherited text in a soft box with a "Copia nel prodotto" button.
 * - When the field is filled, just shows a tiny note that the inherited
 *   value is being overridden.
 * - When neither the field nor the subcategory has content → renders nothing.
 */
function InheritedPreview({ field, fieldLabel, productValue, inheritedValue, sourceName, isEmpty, onCopy, testId }) {
  if (!inheritedValue || isEmpty(inheritedValue)) {
    // Nothing to inherit — silent.
    return null;
  }
  const empty = isEmpty(productValue);
  if (!empty) {
    return (
      <div className="text-xs text-[#A09988] mt-1.5 italic" data-testid={`${testId}-override-note`}>
        Il tuo testo sovrascrive la {fieldLabel} ereditata da {sourceName ? <strong>{sourceName}</strong> : "categoria"}.
      </div>
    );
  }
  return (
    <div className="mt-2 border border-dashed border-[#C5B39B] bg-[#FAF9F6] rounded-sm p-3" data-testid={testId}>
      <div className="flex items-center justify-between gap-3 mb-2">
        <div className="text-[10px] tracking-luxury uppercase text-[#707070]">
          Ereditato da {sourceName || "categoria"}
        </div>
        <button type="button"
                onClick={() => onCopy(inheritedValue)}
                className="text-xs text-[#1A1A1A] hover:text-[#707070] underline"
                data-testid={`${testId}-copy-btn`}>
          Copia nel prodotto
        </button>
      </div>
      <div className="prose-rt text-sm text-[#4a4a4a]"
           dangerouslySetInnerHTML={{ __html: inheritedValue }} />
      <div className="text-[11px] text-[#A09988] mt-2">
        Lascia il campo vuoto per usare automaticamente questo testo sulla pagina QR.
      </div>
    </div>
  );
}

function Caratteristiche({ data, setData, characteristics, characteristicsLoading, characteristicsError, pictograms, pictogramCategories, RatingIcon }) {
  const setRating = (charId, v) => {
    const next = [...(data.characteristic_values || [])];
    const idx = next.findIndex(x => x.characteristic_id === charId);
    if (idx >= 0) next[idx] = { ...next[idx], value_1_to_5: v };
    else next.push({ characteristic_id: charId, value_1_to_5: v });
    setData(d => ({ ...d, characteristic_values: next }));
  };
  const getRating = (charId) => (data.characteristic_values || []).find(x => x.characteristic_id === charId)?.value_1_to_5 || 0;
  const togglePicto = (picto) => setData(d => ({ ...d,
    pictogram_ids: togglePictogramSelection(d.pictogram_ids || [], picto),
  }));

  // Group active pictograms by category
  const activePictos = dedupeSelectablePictograms(pictograms.filter(p => p.is_active));
  const selectedIds = data.pictogram_ids || [];
  const groups = groupPictograms(
    [...pictogramCategories].sort((a, b) => (a.order_index || 0) - (b.order_index || 0)),
    activePictos, "Senza categoria"
  );

  // Track which categories are open. Default: open categories that contain a selected pictogram, otherwise open the first.
  const [openGroups, setOpenGroups] = useState({});
  const [initialised, setInitialised] = useState(false);
  useEffect(() => {
    if (initialised || groups.length === 0) return;
    const next = {};
    let anySelected = false;
    groups.forEach(g => {
      const hasSelected = g.items.some(p => isPictogramSelected(selectedIds, p));
      if (hasSelected) { next[g.id || "none"] = true; anySelected = true; }
    });
    if (!anySelected && groups[0]) next[groups[0].id || "none"] = true;
    setOpenGroups(next);
    setInitialised(true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pictograms, pictogramCategories]);

  const toggleGroup = (key) => setOpenGroups(o => ({ ...o, [key]: !o[key] }));

  return (
    <div className="space-y-8">
      <div>
        <div className="label-tiny mb-3">Caratteristiche tecniche</div>
        {characteristicsLoading && <div className="text-sm text-[#707070]" data-testid="product-characteristics-loading">Caricamento caratteristiche...</div>}
        {!characteristicsLoading && !characteristicsError && characteristics.length === 0 && <div className="text-sm text-[#707070]" data-testid="product-characteristics-empty">Nessuna caratteristica configurata. Creala dagli Archivi.</div>}
        <div className="space-y-4">
          {characteristics.filter(c => c.is_active || (data.characteristic_values || []).some(cv => cv.characteristic_id === c.id)).map(c => (
            <div key={c.id} className="flex items-center justify-between gap-4" data-testid={`product-characteristic-${c.id}`}>
              <div className="flex-1 text-sm" data-testid={`product-characteristic-name-${c.id}`}>{c.name}</div>
              <div className="flex gap-1" data-testid={`rating-${c.id}`}>
                {[1,2,3,4,5].map(v => {
                  const active = v <= getRating(c.id);
                  return (
                    <button key={v} onClick={()=>setRating(c.id, v)} type="button"
                             className="hover:scale-110 transition"
                             aria-label={`Imposta ${c.name} a ${v} su 5`}
                             data-testid={`rating-${c.id}-${v}`}>
                      <RatingIcon className={`w-5 h-5 ${active ? "fill-[#1A1A1A] text-[#1A1A1A]" : "text-[#C5B39B]"}`}
                                  strokeWidth={1.5} />
                    </button>
                  );
                })}
                {getRating(c.id) > 0 && (
                  <button onClick={()=>setRating(c.id, 0)} className="ml-2 text-xs text-[#707070]"
                          type="button" data-testid={`rating-${c.id}-clear`}>×</button>
                )}
              </div>
              <span className="text-xs text-[#525252] tabular-nums min-w-7 text-right" data-testid={`product-characteristic-value-${c.id}`}>{getRating(c.id)}/5</span>
            </div>
          ))}
        </div>
      </div>
      <div>
        <div className="label-tiny mb-3">Pittogrammi / Smaltimento</div>
        {activePictos.length === 0 && <div className="text-sm text-[#707070]">Nessun pittogramma configurato.</div>}
        <div className="space-y-2" data-testid="picto-accordion">
          {groups.map(g => {
            const key = g.id || "none";
            const isOpen = !!openGroups[key];
            const selectedCount = g.items.filter(p => isPictogramSelected(selectedIds, p)).length;
            return (
              <div key={key} className="border border-[#E5E5E0] rounded-sm bg-white" data-testid={`picto-group-${key}`}>
                <button
                  type="button"
                  onClick={() => toggleGroup(key)}
                  className="w-full flex flex-wrap sm:flex-nowrap items-center justify-between gap-2 sm:gap-3 px-4 py-3 hover:bg-[#F9F9F5] transition"
                  data-testid={`picto-group-toggle-${key}`}
                  aria-expanded={isOpen}
                >
                  <div className="flex flex-wrap sm:flex-nowrap items-center gap-x-2 sm:gap-x-3 gap-y-1 flex-1 min-w-0">
                    <ChevronDown
                      className={`w-4 h-4 shrink-0 text-[#707070] transition-transform duration-200 ${isOpen ? "rotate-0" : "-rotate-90"}`}
                      strokeWidth={1.5}
                    />
                    <span className="font-serif text-base text-[#1A1A1A] min-w-0 break-words">{g.name}</span>
                    <span className="text-xs text-[#707070] tracking-luxury uppercase w-full pl-6 sm:w-auto sm:pl-0">
                      {g.items.length} pittogramm{g.items.length === 1 ? "a" : "i"}
                    </span>
                  </div>
                  {selectedCount > 0 && (
                    <span className="text-xs bg-[#1A1A1A] text-white px-2 py-0.5 rounded-sm shrink-0 max-w-full" data-testid={`picto-group-count-${key}`}>
                      {selectedCount} selezionat{selectedCount === 1 ? "o" : "i"}
                    </span>
                  )}
                </button>
                {isOpen && (
                  <div className="px-4 pb-4 pt-1">
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                      {g.items.map(p => {
                        const active = isPictogramSelected(selectedIds, p);
                        return (
                          <button key={p.id} type="button" onClick={()=>togglePicto(p)}
                                   className={`p-3 border rounded-sm flex flex-col items-center gap-2 transition ${active ? "border-[#1A1A1A] bg-[#F4F4F0]" : "border-[#E5E5E0] hover:border-[#C5B39B]"}`}
                                   aria-pressed={active} data-testid={`picto-toggle-${p.id}`}>
                            {p.icon_file_id ? (
                              <PictogramImage pictogram={p} className="w-10 h-10" testId={`product-picto-image-${p.id}`} />
                            ) : <div className="w-10 h-10 bg-[#E5E5E0]" />}
                            <div className="w-full min-w-0 text-xs text-center [overflow-wrap:anywhere]" data-testid={`product-picto-label-${p.id}`}>{p.name}</div>
                            {p.is_default && <span className="text-[10px] text-[#707070]" data-testid={`product-picto-default-${p.id}`}>Default</span>}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function InciTab({ pid, isNew, ensureSaved, inciFile, setInciFile, ingredients, setIngredients, reload }) {
  const { user, canWrite } = useAuth();
  const isAdmin = canWrite;
  const [busy, setBusy] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);
  const [overIdx, setOverIdx] = useState(null);
  const [cpnpOpen, setCpnpOpen] = useState(false);
  const [cpnpLangs, setCpnpLangs] = useState({ en: true, fr: false, es: false, de: false, pt: false });
  const [cpnpBusy, setCpnpBusy] = useState(false);
  const [creationDate, setCreationDate] = useState("");
  const [revisionDate, setRevisionDate] = useState("");
  const [versionPromptOpen, setVersionPromptOpen] = useState(false);
  const [versionDraft, setVersionDraft] = useState("");
  const [analysis, setAnalysis] = useState(null);
  const [analysisUpdatedAt, setAnalysisUpdatedAt] = useState(null);
  const [analysisBusy, setAnalysisBusy] = useState(false);
  const fileRef = useRef();
  const inciBaseline = useRef(null);

  // Capture the loaded INCI state as baseline on product/file load (not on edits).
  useEffect(() => {
    inciBaseline.current = JSON.stringify({
      ings: ingredients,
      cd: (inciFile?.creation_date || "").toString(),
      rd: (inciFile?.revision_date || "").toString(),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pid, inciFile?.id]);

  // Load cached analysis on mount / when switching product.
  useEffect(() => {
    if (!pid || !isAdmin) return;
    api.get(`/products/${pid}/inci/analysis`).then(r => {
      setAnalysis(r.data?.analysis || null);
      setAnalysisUpdatedAt(r.data?.updated_at || null);
    }).catch(() => {});
  }, [pid, isAdmin]);

  const runAnalysis = async () => {
    if (!pid) return;
    setAnalysisBusy(true);
    try {
      const r = await api.post(`/products/${pid}/inci/analyze`);
      setAnalysis(r.data?.analysis || null);
      setAnalysisUpdatedAt(new Date().toISOString());
      toast.success("Analisi INCI aggiornata");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Errore analisi INCI";
      toast.error(msg);
    } finally { setAnalysisBusy(false); }
  };

  // Keep the local date inputs in sync with whatever the server has.
  useEffect(() => {
    setCreationDate((inciFile?.creation_date || "").toString());
    setRevisionDate((inciFile?.revision_date || "").toString());
  }, [inciFile?.creation_date, inciFile?.revision_date]);

  // Format an ISO date (YYYY-MM-DD) as DD/MM/YYYY for display; pass through otherwise.
  const fmtDateIt = (v) => {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec((v || "").trim());
    return m ? `${m[3]}/${m[2]}/${m[1]}` : (v || "");
  };

  const upload = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (!checkFileSize(f)) { e.target.value = ""; return; }
    // If we already have ingredients on this product, warn the admin that
    // uploading a new sheet will replace them all. The backend delete_many
    // runs during /inci/extract, so this is a destructive action.
    if ((ingredients || []).length > 0) {
      const ok = window.confirm(
        "Caricando una nuova scheda INCI, TUTTI gli ingredienti attualmente presenti verranno cancellati e sostituiti con quelli estratti dal nuovo file.\n\nVuoi procedere?"
      );
      if (!ok) { e.target.value = ""; return; }
    }
    // Auto-save the product if it's not yet persisted
    const productId = pid || await ensureSaved();
    if (!productId) { e.target.value = ""; return; }
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      await api.post(`/products/${productId}/inci/upload`, fd, { headers: {"Content-Type":"multipart/form-data"}});
      toast.success("File INCI caricato. Avvio estrazione AI...");
      const r = await api.post(`/products/${productId}/inci/extract`);
      setIngredients(r.data.items);
      setInciFile({ status: r.data.status, creation_date: r.data.creation_date || null, revision_date: r.data.revision_date || null });
      setCreationDate((r.data.creation_date || "").toString());
      setRevisionDate((r.data.revision_date || "").toString());
      // Extract endpoint runs analysis in background — refetch to get results.
      if (isAdmin) {
        api.get(`/products/${productId}/inci/analysis`).then(res => {
          setAnalysis(res.data?.analysis || null);
          setAnalysisUpdatedAt(res.data?.updated_at || null);
        }).catch(() => {});
      }
      if (r.data.creation_date) {
        const bits = [`Data creazione: ${fmtDateIt(r.data.creation_date)}`];
        if (r.data.revision_date) bits.push(`Ultima revisione: ${fmtDateIt(r.data.revision_date)}`);
        toast.success(`Estratti ${r.data.items.length} ingredienti · ${bits.join(" · ")}`);
      } else {
        toast.success(`Estratti ${r.data.items.length} ingredienti`);
        // Auto-prompt for the missing creation date so the admin doesn't forget.
        setVersionDraft("");
        setVersionPromptOpen(true);
      }
    } catch (err) { toast.error("Errore upload/estrazione INCI"); }
    finally { setBusy(false); fileRef.current.value = ""; }
  };

  const updateRow = (i, k, v) => {
    const next = [...ingredients];
    next[i] = { ...next[i], [k]: v };
    setIngredients(next);
  };
  const removeRow = (i) => setIngredients(ingredients.filter((_, idx) => idx !== i));
  const addRow = () => setIngredients([...ingredients, { order_index: ingredients.length+1, ingredient_name: "", cas: "", range_raw: "" }]);
  const moveRow = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= ingredients.length) return;
    const next = [...ingredients];
    [next[i], next[j]] = [next[j], next[i]];
    next.forEach((r, idx) => r.order_index = idx + 1);
    setIngredients(next);
  };

  const onDragStart = (i) => (e) => {
    setDragIdx(i);
    e.dataTransfer.effectAllowed = "move";
  };
  const onDragOver = (i) => (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (overIdx !== i) setOverIdx(i);
  };
  const onDrop = (i) => (e) => {
    e.preventDefault();
    if (dragIdx === null || dragIdx === i) { setDragIdx(null); setOverIdx(null); return; }
    const next = [...ingredients];
    const [moved] = next.splice(dragIdx, 1);
    next.splice(i, 0, moved);
    next.forEach((r, idx) => r.order_index = idx + 1);
    setIngredients(next);
    setDragIdx(null);
    setOverIdx(null);
  };
  const onDragEnd = () => { setDragIdx(null); setOverIdx(null); };

  const saveRows = async ({ silent = false, creationOverride, revisionOverride } = {}) => {
    setBusy(true);
    try {
      const items = ingredients.map((r, i) => ({ ...r, order_index: i+1 }));
      const cd = (creationOverride !== undefined ? creationOverride : creationDate) || "";
      const rd = (revisionOverride !== undefined ? revisionOverride : revisionDate) || "";
      const r = await api.put(`/products/${pid}/inci/rows`, { items, creation_date: cd, revision_date: rd });
      if (!silent) toast.success("Ingredienti salvati");
      setInciFile(prev => ({ ...(prev || {}), creation_date: cd || null, revision_date: rd || null }));
      if (r.data?.analysis) {
        setAnalysis(r.data.analysis);
        setAnalysisUpdatedAt(new Date().toISOString());
      }
    } catch { toast.error("Errore salvataggio"); } finally { setBusy(false); }
  };

  // Unsaved-changes guard for the INCI section (ingredients rows + dates).
  const inciDirty = canWrite && inciBaseline.current !== null && inciBaseline.current !== JSON.stringify({
    ings: ingredients,
    cd: (creationDate || "").toString(),
    rd: (revisionDate || "").toString(),
  });
  useUnsavedChanges(inciDirty, async () => {
    await saveRows({ silent: true });
    inciBaseline.current = JSON.stringify({
      ings: ingredients, cd: (creationDate || "").toString(), rd: (revisionDate || "").toString(),
    });
    return true;
  }, () => {
    // Discard: revert rows + dates to the last-loaded/saved INCI baseline.
    if (!inciBaseline.current) return;
    const base = JSON.parse(inciBaseline.current);
    setIngredients(base.ings || []);
    setCreationDate(base.cd || "");
    setRevisionDate(base.rd || "");
  });

  const approve = async () => {
    // Require the creation date before publishing. If missing, open the modal.
    if (!creationDate.trim()) {
      setVersionDraft("");
      setVersionPromptOpen(true);
      return;
    }
    await saveRows({ silent: true });
    try {
      await api.post(`/products/${pid}/inci/approve`);
      toast.success("INCI approvato. Sarà visibile sulla pagina QR.");
      reload();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Errore approvazione";
      toast.error(msg);
    }
  };

  const deleteInci = async () => {
    if (!pid) return;
    const ok = window.confirm(
      "Vuoi cancellare COMPLETAMENTE la scheda INCI di questo prodotto?\n\nVerranno eliminati tutti gli ingredienti, il file caricato e le date. L'operazione non è reversibile.\n\nPotrai poi caricare una nuova scheda da zero."
    );
    if (!ok) return;
    setBusy(true);
    try {
      await api.delete(`/products/${pid}/inci`);
      setIngredients([]);
      setInciFile(null);
      setCreationDate("");
      setRevisionDate("");
      setAnalysis(null);
      setAnalysisUpdatedAt(null);
      toast.success("Scheda INCI cancellata. Puoi caricarne una nuova.");
    } catch {
      toast.error("Errore durante la cancellazione della scheda INCI");
    } finally { setBusy(false); }
  };

  const confirmVersionAndApprove = async () => {
    const v = versionDraft.trim();
    if (!v) { toast.error("Inserisci la data di creazione"); return; }
    setCreationDate(v);
    setVersionPromptOpen(false);
    await saveRows({ silent: true, creationOverride: v });
    try {
      await api.post(`/products/${pid}/inci/approve`);
      toast.success("INCI approvato. Sarà visibile sulla pagina QR.");
      reload();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Errore approvazione";
      toast.error(msg);
    }
  };

  const downloadCpnp = async () => {
    try {
      setCpnpBusy(true);
      const langs = Object.entries(cpnpLangs).filter(([, v]) => v).map(([k]) => k).join(",");
      const token = getActiveToken();
      // Cache-buster so a CDN/browser never returns a stale PDF after INCI edits.
      const parts = [`t=${Date.now()}`];
      // Always send langs (even empty) so unchecking every language yields an Italian-only sheet
      // instead of falling back to the backend default ("en").
      parts.push(`langs=${encodeURIComponent(langs)}`);
      const qs = `?${parts.join("&")}`;
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/products/${pid}/cpnp.pdf${qs}`,
        { headers: { Authorization: `Bearer ${token}` }, cache: "no-store" }
      );
      if (!res.ok) throw new Error("cpnp generation failed");
      const blob = await res.blob();
      const cd = res.headers.get("content-disposition") || "";
      const m = /filename="?([^"]+)"?/i.exec(cd);
      const fname = m ? m[1] : "cpnp.pdf";
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = fname;
      a.click();
      URL.revokeObjectURL(a.href);
      setCpnpOpen(false);
    } catch {
      toast.error("Impossibile generare la scheda CPNP");
    } finally {
      setCpnpBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="inci-tab">
      <div className="flex items-start justify-between gap-6 flex-wrap">
        <div className="space-y-3 min-w-[280px]">
          <div>
            <div className="label-tiny mb-1">File INCI</div>
            <div className="text-sm" data-testid="product-inci-file-status">
              {inciFile ? (
                <span>Stato: <ProductStatus tone={INCI_STATES[inciFile.status]?.tone || "neutral"}
                       icon={inciFile.status === "approved" ? CheckCircle2 : undefined} testId="product-inci-status">
                  {INCI_STATES[inciFile.status]?.label || inciFile.status}
                </ProductStatus>
                </span>
              ) : <ProductStatus tone="warning" testId="product-inci-status">Mancante</ProductStatus>}
            </div>
          </div>
          <UploadLimitNote />
          {inciFile && (
            <div className="space-y-3">
              <div>
                <label className="label-tiny mb-1 block" htmlFor="inci-creation-date-input">
                  Data creazione <span className="text-[#8C3A3A]">*</span>
                </label>
                <input
                  id="inci-creation-date-input"
                  type="date"
                  value={creationDate}
                  onChange={(e) => setCreationDate(e.target.value)}
                  onBlur={() => { if ((creationDate || "") !== ((inciFile?.creation_date || "").toString())) saveRows({ silent: true }); }}
                  className={`input-luxury text-sm ${!creationDate.trim() ? "border-[#8C3A3A]" : ""}`}
                  data-testid="inci-creation-date-input"
                />
                {!creationDate.trim() && (
                  <div className="text-xs text-[#8C3A3A] mt-1">Obbligatoria per approvare l'INCI</div>
                )}
              </div>
              <div>
                <label className="label-tiny mb-1 block" htmlFor="inci-revision-date-input">
                  Data ultima revisione
                </label>
                <input
                  id="inci-revision-date-input"
                  type="date"
                  value={revisionDate}
                  onChange={(e) => setRevisionDate(e.target.value)}
                  onBlur={() => { if ((revisionDate || "") !== ((inciFile?.revision_date || "").toString())) saveRows({ silent: true }); }}
                  className="input-luxury text-sm"
                  data-testid="inci-revision-date-input"
                />
                <div className="text-xs text-[#707070] mt-1">Compila solo se il documento è stato revisionato</div>
              </div>
            </div>
          )}
        </div>
        <div className="flex gap-3">
          <input ref={fileRef} type="file" accept=".pdf,.docx,.xlsx" onChange={upload}
                 className="hidden" data-testid="inci-file-input" />
          <button onClick={()=>fileRef.current.click()} disabled={busy} className="btn-ghost flex items-center" data-testid="inci-upload-btn">
            <Upload className="w-3 h-3 mr-2" strokeWidth={1.5} />{busy ? "Elaborazione..." : "Carica & Estrai"}
          </button>
          {(inciFile || ingredients.length > 0) && (
            <button onClick={deleteInci} disabled={busy}
                    className="btn-ghost flex items-center text-[#8C3A3A] hover:opacity-70"
                    data-testid="inci-delete-btn"
                    title="Cancella completamente la scheda INCI (ingredienti, file e date)">
              <Trash2 className="w-3 h-3 mr-2" strokeWidth={1.5} />Cancella scheda INCI
            </button>
          )}
        </div>
      </div>

      {ingredients.length > 0 && (
        <>
          <table className="w-full text-sm border-t border-[#E5E5E0]" data-testid="inci-table">
            <thead>
              <tr className="text-xs uppercase tracking-luxury text-[#707070]">
                <th className="py-2 text-left w-12">#</th>
                <th className="text-left">Ingrediente</th>
                <th className="text-left w-32">CAS</th>
                <th className="text-left w-32">Range %</th>
                <th className="w-32"></th>
              </tr>
            </thead>
            <tbody>
              {ingredients.map((r, i) => (
                <tr key={i}
                    draggable
                    onDragStart={onDragStart(i)}
                    onDragOver={onDragOver(i)}
                    onDrop={onDrop(i)}
                    onDragEnd={onDragEnd}
                    className={`border-t border-[#E5E5E0] transition ${dragIdx === i ? "opacity-40" : ""} ${overIdx === i && dragIdx !== i ? "bg-[#F4F4F0]" : ""}`}
                    data-testid={`inci-row-${i}`}>
                  <td className="py-2 cursor-move text-[#C5B39B] select-none">
                    <span className="inline-flex items-center gap-1.5">
                      <GripVertical className="w-3.5 h-3.5" strokeWidth={1.5} />
                      <span className="text-[#1A1A1A]">{i+1}</span>
                    </span>
                  </td>
                  <td className="py-2 pr-2">
                    <input className="input-luxury" value={r.ingredient_name || ""}
                           onChange={(e)=>updateRow(i, "ingredient_name", e.target.value)}
                           data-testid={`inci-name-${i}`} />
                  </td>
                  <td className="py-2 pr-2">
                    <input className="input-luxury" value={r.cas || ""}
                           onChange={(e)=>updateRow(i, "cas", e.target.value)}
                           data-testid={`inci-cas-${i}`} />
                  </td>
                  <td className="py-2 pr-2">
                    <input className="input-luxury" value={r.range_raw || ""}
                           placeholder="es. 1-5%"
                           onChange={(e)=>updateRow(i, "range_raw", e.target.value)}
                           data-testid={`inci-range-${i}`} />
                  </td>
                  <td className="py-2 text-right">
                    <button onClick={()=>moveRow(i,-1)} className="text-xs px-2 hover:text-[#1A1A1A] text-[#707070]" type="button" title="Sposta su">↑</button>
                    <button onClick={()=>moveRow(i,1)} className="text-xs px-2 hover:text-[#1A1A1A] text-[#707070]" type="button" title="Sposta giù">↓</button>
                    <button onClick={()=>removeRow(i)} className="text-xs px-2 text-[#8C3A3A] hover:opacity-70" type="button" data-testid={`inci-remove-${i}`}>×</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="flex gap-3">
            <button onClick={addRow} className="btn-ghost" data-testid="inci-add-row-btn">+ Aggiungi riga</button>
            <button onClick={() => setCpnpOpen(true)} className="btn-ghost flex items-center" data-testid="inci-cpnp-btn"
                    title="Scarica scheda CPNP (PDF)">
              <FileText className="w-3 h-3 mr-2" strokeWidth={1.5} />Scarica CPNP/INCI
            </button>
            {inciFile?.status === "approved" ? (
              <button onClick={saveRows} className="btn-primary flex items-center" data-testid="inci-save-btn"
                      title="Salva le modifiche agli ingredienti">
                <CheckCircle2 className="w-3 h-3 mr-2" strokeWidth={2} />Salva modifiche
              </button>
            ) : (
              <button onClick={approve} className="btn-primary flex items-center" data-testid="inci-approve-btn"
                       title="Salva e rendi visibile l'INCI sulla pagina pubblica QR">
                <CheckCircle2 className="w-3 h-3 mr-2" strokeWidth={2} />Salva e Approva INCI
              </button>
            )}
          </div>
          {isAdmin && (
            <InciAnalysisPanel analysis={analysis} updatedAt={analysisUpdatedAt}
                               onRefresh={runAnalysis} busy={analysisBusy} />
          )}
        </>
      )}
      {versionPromptOpen && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center px-4"
             onClick={() => setVersionPromptOpen(false)}
             data-testid="inci-version-modal">
          <div className="bg-white rounded-sm shadow-xl max-w-md w-full p-6"
               onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-1">
              <h3 className="font-serif text-2xl">Data creazione INCI</h3>
              <button onClick={() => setVersionPromptOpen(false)}
                      className="text-[#707070] hover:text-[#1A1A1A]"
                      data-testid="inci-version-modal-close"><X className="w-4 h-4"/></button>
            </div>
            <p className="text-sm text-[#707070] mb-4">
              Non è stato possibile rilevare automaticamente la data di creazione della scheda INCI. Inseriscila manualmente (obbligatoria per approvare). La data di ultima revisione, se presente, si compila nel pannello.
            </p>
            <input
              type="date"
              autoFocus
              value={versionDraft}
              onChange={(e) => setVersionDraft(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") confirmVersionAndApprove(); }}
              className="input-luxury text-sm w-full mb-5"
              data-testid="inci-version-modal-input"
            />
            <div className="flex justify-end gap-3">
              <button onClick={() => setVersionPromptOpen(false)} className="btn-ghost"
                      data-testid="inci-version-modal-cancel">Annulla</button>
              <button onClick={confirmVersionAndApprove} className="btn-primary flex items-center"
                      disabled={!versionDraft.trim()}
                      data-testid="inci-version-modal-confirm">
                <CheckCircle2 className="w-3 h-3 mr-2" strokeWidth={2}/>Salva e Approva
              </button>
            </div>
          </div>
        </div>
      )}
      {cpnpOpen && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center px-4"
             onClick={() => !cpnpBusy && setCpnpOpen(false)}
             data-testid="cpnp-lang-modal">
          <div className="bg-white rounded-sm shadow-xl max-w-md w-full p-6"
               onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-1">
              <h3 className="font-serif text-2xl">Esporta CPNP/INCI</h3>
              <button onClick={() => !cpnpBusy && setCpnpOpen(false)}
                      className="text-[#707070] hover:text-[#1A1A1A]"
                      data-testid="cpnp-close-btn"><X className="w-4 h-4"/></button>
            </div>
            <p className="text-sm text-[#707070] mb-5">
              L'italiano è sempre incluso. Seleziona le lingue in cui tradurre la sezione "Avvertenze".
            </p>
            <div className="space-y-2 mb-6">
              {[
                { code: "en", label: "Inglese", native: "English" },
                { code: "fr", label: "Francese", native: "Français" },
                { code: "es", label: "Spagnolo", native: "Español" },
                { code: "de", label: "Tedesco", native: "Deutsch" },
                { code: "pt", label: "Portoghese", native: "Português" },
              ].map(l => (
                <label key={l.code}
                       className={`flex items-center gap-3 p-3 border rounded-sm cursor-pointer transition ${cpnpLangs[l.code] ? "border-[#1A1A1A] bg-[#F4F4F0]" : "border-[#E5E5E0] hover:border-[#C5B39B]"}`}
                       data-testid={`cpnp-lang-${l.code}`}>
                  <input type="checkbox" checked={!!cpnpLangs[l.code]}
                         onChange={(e) => setCpnpLangs(s => ({ ...s, [l.code]: e.target.checked }))}
                         data-testid={`cpnp-lang-${l.code}-check`} />
                  <span className="flex-1 text-sm">{l.label} <span className="text-[#A0A0A0]">· {l.native}</span></span>
                  <span className="text-[10px] tracking-luxury uppercase text-[#A0A0A0]">{l.code}</span>
                </label>
              ))}
            </div>
            <div className="flex justify-end gap-3">
              <button onClick={() => setCpnpOpen(false)} className="btn-ghost" disabled={cpnpBusy}
                      data-testid="cpnp-cancel-btn">Annulla</button>
              <button onClick={downloadCpnp} className="btn-primary flex items-center" disabled={cpnpBusy}
                      data-testid="cpnp-download-btn">
                <Download className="w-3 h-3 mr-2" strokeWidth={1.5}/>
                {cpnpBusy ? "Generazione..." : "Genera PDF"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function FotoTab({ pid, isNew, ensureSaved, photos, setPhotos, labels, setLabels, reload }) {
  const fileRef = useRef();
  const labelRef = useRef();
  const [dragIdx, setDragIdx] = useState(null);
  const [overIdx, setOverIdx] = useState(null);
  const [previewLabel, setPreviewLabel] = useState(null);

  const downloadFile = async (url, filename) => {
    try {
      const token = getActiveToken();
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}${url}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("download failed");
      const blob = await res.blob();
      const cd = res.headers.get("content-disposition") || "";
      const m = /filename="?([^"]+)"?/i.exec(cd);
      const fname = m ? m[1] : filename;
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = fname;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(a.href);
    } catch {
      toast.error("Errore download");
    }
  };

  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    for (const f of files) { if (!checkFileSize(f)) { e.target.value = ""; return; } }
    const productId = pid || await ensureSaved();
    if (!productId) { e.target.value = ""; return; }
    for (const f of files) {
      const fd = new FormData(); fd.append("file", f);
      await api.post(`/products/${productId}/photos`, fd);
    }
    toast.success("Foto caricate (convertite in WEBP)");
    reload(productId);
    fileRef.current.value = "";
  };
  const setPrimary = async (photoId) => {
    await api.put(`/products/${pid}/photos/${photoId}/set-primary`);
    reload();
  };
  const del = async (photoId) => {
    await api.delete(`/products/${pid}/photos/${photoId}`);
    reload();
  };
  const persistOrder = async (orderedIds) => {
    try {
      await api.put(`/products/${pid}/photos/order`, { photo_ids: orderedIds });
      toast.success("Ordine foto aggiornato");
    } catch { toast.error("Errore aggiornamento ordine"); reload(); }
  };
  const onPhotoDragStart = (i) => (e) => {
    setDragIdx(i);
    e.dataTransfer.effectAllowed = "move";
  };
  const onPhotoDragOver = (i) => (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (overIdx !== i) setOverIdx(i);
  };
  const onPhotoDrop = (i) => (e) => {
    e.preventDefault();
    if (dragIdx === null || dragIdx === i) { setDragIdx(null); setOverIdx(null); return; }
    const next = [...photos];
    const [moved] = next.splice(dragIdx, 1);
    next.splice(i, 0, moved);
    setPhotos(next);
    persistOrder(next.map(p => p.id));
    setDragIdx(null);
    setOverIdx(null);
  };
  const onPhotoDragEnd = () => { setDragIdx(null); setOverIdx(null); };

  const uploadLabel = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (!checkFileSize(f)) { e.target.value = ""; return; }
    const productId = pid || await ensureSaved();
    if (!productId) { e.target.value = ""; return; }
    const fd = new FormData(); fd.append("file", f); fd.append("label_type", "generic");
    await api.post(`/products/${productId}/labels`, fd);
    toast.success("Etichetta caricata");
    reload(productId);
    labelRef.current.value = "";
  };
  const delLabel = async (labelId) => {
    await api.delete(`/products/${pid}/labels/${labelId}`);
    reload();
  };

  return (
    <div className="space-y-8" data-testid="foto-tab">
      <div>
        <div className="flex justify-between items-center mb-4">
          <div>
            <div className="label-tiny mb-0">Foto prodotto (WEBP)</div>
            <UploadLimitNote />
            {photos.length > 1 && <div className="text-xs text-[#707070] mt-1">Trascina per riordinare. La prima diventa la principale di default.</div>}
          </div>
          <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" multiple
                 onChange={upload} className="hidden" data-testid="photo-input" />
          <button onClick={()=>fileRef.current.click()} className="btn-ghost flex items-center" data-testid="photo-upload-btn">
            <Upload className="w-3 h-3 mr-2" strokeWidth={1.5} />Carica foto
          </button>
        </div>
        {photos.length === 0 && <div className="text-sm text-[#707070]">Nessuna foto. JPG/PNG/WEBP verranno convertite in WEBP.</div>}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {photos.map((ph, i) => (
            <div key={ph.id}
                 draggable
                 onDragStart={onPhotoDragStart(i)}
                 onDragOver={onPhotoDragOver(i)}
                 onDrop={onPhotoDrop(i)}
                 onDragEnd={onPhotoDragEnd}
                 className={`relative group border cursor-move transition ${ph.is_primary ? "border-[#1A1A1A]" : "border-[#E5E5E0]"} ${dragIdx === i ? "opacity-40 scale-95" : ""} ${overIdx === i && dragIdx !== i ? "ring-2 ring-[#C5B39B] ring-offset-2" : ""}`}
                 data-testid={`photo-card-${ph.id}`}>
              <img src={`${process.env.REACT_APP_BACKEND_URL}${ph.url}`} className="w-full aspect-square object-cover pointer-events-none" />
              <div className="absolute top-2 right-2 bg-white/85 backdrop-blur p-1 rounded-sm opacity-0 group-hover:opacity-100 transition">
                <GripVertical className="w-4 h-4 text-[#1A1A1A]" strokeWidth={1.5} />
              </div>
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
                {!ph.is_primary && (
                  <button onClick={()=>setPrimary(ph.id)} className="text-xs bg-white px-2 py-1" data-testid={`photo-primary-${ph.id}`}>
                    Imposta principale
                  </button>
                )}
                <button onClick={()=>downloadFile(`/api/products/${pid}/photos/${ph.id}/download`, "foto.webp")}
                        className="text-xs bg-white px-2 py-1 inline-flex items-center gap-1"
                        data-testid={`photo-download-${ph.id}`}
                        title="Scarica foto">
                  <Download className="w-3 h-3" strokeWidth={1.5}/>
                </button>
                <button onClick={()=>del(ph.id)} className="text-xs bg-white px-2 py-1 text-[#8C3A3A]" data-testid={`photo-delete-${ph.id}`}>
                  Elimina
                </button>
              </div>
              {ph.is_primary && (
                <div className="absolute top-2 left-2 bg-[#1A1A1A] text-[#FAF9F6] text-[10px] tracking-luxury uppercase px-2 py-1">Principale</div>
              )}
            </div>
          ))}
        </div>
      </div>
      <div>
        <div className="flex justify-between items-center mb-4">
          <div>
            <div className="label-tiny mb-0">Etichette (solo admin)</div>
            <div className="text-xs text-[#707070]">PDF, JPG, PNG</div>
            <UploadLimitNote />
          </div>
          <input ref={labelRef} type="file" accept=".pdf,image/jpeg,image/png,image/webp"
                 onChange={uploadLabel} className="hidden" data-testid="label-input" />
          <button onClick={()=>labelRef.current.click()} className="btn-ghost flex items-center" data-testid="label-upload-btn">
            <Upload className="w-3 h-3 mr-2" strokeWidth={1.5} />Carica etichetta
          </button>
        </div>
        <div className="space-y-2">
          {labels.map(lb => {
            const isPdf = (lb.mime_type || "").includes("pdf");
            const isImage = (lb.mime_type || "").startsWith("image/");
            return (
              <div key={lb.id} className="flex justify-between items-center py-2 border-b border-[#E5E5E0] text-sm gap-3"
                   data-testid={`label-row-${lb.id}`}>
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <FileText className="w-4 h-4 text-[#707070] shrink-0" strokeWidth={1.5}/>
                  <span className="truncate" title={lb.original_filename}>
                    {lb.original_filename || `${lb.label_type} — ${lb.id.slice(0,8)}`}
                  </span>
                  {isPdf && <span className="text-[10px] uppercase tracking-luxury text-[#707070] shrink-0">PDF</span>}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {(isPdf || isImage) && (
                    <button onClick={()=>setPreviewLabel(lb)}
                            className="text-xs px-2 py-1 hover:text-[#1A1A1A] text-[#707070] inline-flex items-center gap-1"
                            data-testid={`label-preview-${lb.id}`}
                            title="Anteprima">
                      <Search className="w-3 h-3" strokeWidth={1.5}/>
                      Anteprima
                    </button>
                  )}
                  <button onClick={()=>downloadFile(`/api/products/${pid}/labels/${lb.id}/download`, lb.original_filename || "etichetta.pdf")}
                          className="text-xs px-2 py-1 hover:text-[#1A1A1A] text-[#707070] inline-flex items-center gap-1"
                          data-testid={`label-download-${lb.id}`}
                          title="Scarica">
                    <Download className="w-3 h-3" strokeWidth={1.5}/>
                    Scarica
                  </button>
                  <button onClick={()=>delLabel(lb.id)} className="text-xs text-[#8C3A3A] hover:opacity-70 px-2 py-1"
                          data-testid={`label-delete-${lb.id}`}>Elimina</button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      {previewLabel && <LabelPreviewModal label={previewLabel} onClose={()=>setPreviewLabel(null)} />}
    </div>
  );
}

function LabelPreviewModal({ label, onClose }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const isPdf = (label.mime_type || "").includes("pdf");

  useEffect(() => {
    let cancelled = false;
    let currentUrl = null;
    (async () => {
      try {
        const token = getActiveToken();
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}${label.url}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error("preview failed");
        const blob = await res.blob();
        currentUrl = URL.createObjectURL(blob);
        if (!cancelled) setBlobUrl(currentUrl);
      } catch {
        toast.error("Impossibile aprire l'anteprima");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; if (currentUrl) URL.revokeObjectURL(currentUrl); };
  }, [label.url]);

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center px-4"
         onClick={onClose} data-testid="label-preview-modal">
      <div className="bg-white rounded-sm shadow-xl w-full max-w-5xl h-[90vh] flex flex-col"
           onClick={(e)=>e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-[#E5E5E0] shrink-0">
          <div className="min-w-0 flex-1">
            <h3 className="font-serif text-xl truncate">{label.original_filename || "Etichetta"}</h3>
            <div className="text-xs text-[#707070] mt-0.5">{label.mime_type}</div>
          </div>
          <button onClick={onClose} className="text-[#707070] hover:text-[#1A1A1A] shrink-0"
                  data-testid="label-preview-close">
            <X className="w-4 h-4"/>
          </button>
        </div>
        <div className="flex-1 bg-[#F4F4F0] overflow-auto">
          {loading ? (
            <div className="h-full flex items-center justify-center text-sm text-[#707070]">
              Caricamento...
            </div>
          ) : blobUrl ? (
            isPdf ? (
              <iframe src={blobUrl} title="Anteprima etichetta"
                      className="w-full h-full border-0" data-testid="label-preview-iframe" />
            ) : (
              <div className="w-full h-full flex items-center justify-center p-4">
                <img src={blobUrl} alt="Anteprima etichetta" className="max-w-full max-h-full object-contain"
                     data-testid="label-preview-image" />
              </div>
            )
          ) : (
            <div className="h-full flex items-center justify-center text-sm text-[#8C3A3A]">
              Anteprima non disponibile.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function BarcodeTab({ pid, isNew, ensureSaved, data, setData, reload }) {
  const ref = useRef();
  const [extracted, setExtracted] = useState(null);
  const [busy, setBusy] = useState(false);
  const [manual, setManual] = useState(data.barcode_number || "");
  // "Confermato" only when a real barcode number exists (guards against legacy
  // duplicated products that inherited barcode_confirmed=true with no number).
  const isConfirmed = data.barcode_confirmed && (data.barcode_number || "").trim();

  const upload = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (!checkFileSize(f)) { e.target.value = ""; return; }
    const productId = pid || await ensureSaved();
    if (!productId) { e.target.value = ""; return; }
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      await api.post(`/products/${productId}/barcode/upload`, fd);
      toast.info("File caricato, avvio estrazione...");
      const r = await api.post(`/products/${productId}/barcode/extract`);
      setExtracted(r.data);
      if (r.data.barcode_number) setManual(r.data.barcode_number);
    } catch { toast.error("Errore estrazione"); }
    finally { setBusy(false); ref.current.value = ""; }
  };
  const confirm = async () => {
    if (!manual.trim()) { toast.error("Inserisci il numero"); return; }
    const productId = pid || await ensureSaved();
    if (!productId) return;
    await api.post(`/products/${productId}/barcode/confirm`, { barcode_number: manual.trim() });
    setData(d => ({ ...d, barcode_number: manual.trim(), barcode_confirmed: true }));
    toast.success("Barcode confermato");
    reload(productId);
  };

  return (
    <div className="space-y-6" data-testid="barcode-tab">
      <div>
        <div className="label-tiny mb-2">File barcode (PDF, EPS)</div>
        <input ref={ref} type="file" accept=".pdf,.eps" onChange={upload} className="hidden" data-testid="barcode-input" />
        <button onClick={()=>ref.current.click()} className="btn-ghost" disabled={busy} data-testid="barcode-upload-btn">
          {busy ? "Estrazione AI..." : "Carica & Estrai"}
        </button>
        <UploadLimitNote className="mt-2" />
      </div>
      {extracted && (
        <div className="text-sm text-[#707070]">
          Confidenza estrazione: <strong>{Math.round((extracted.confidence||0)*100)}%</strong>
        </div>
      )}
      <div>
        <label className="label-tiny">Numero barcode</label>
        <input className="input-luxury" value={manual} onChange={(e)=>setManual(e.target.value)}
               data-testid="input-barcode-number" placeholder="Inserisci o correggi manualmente" />
      </div>
      <div className="flex items-center gap-3">
        <button onClick={confirm} className="btn-primary" data-testid="barcode-confirm-btn">
          {isConfirmed ? "Aggiorna" : "Conferma barcode"}
        </button>
        {isConfirmed && (
          <span className="text-xs text-[#4A6B53] flex items-center"><CheckCircle2 className="w-4 h-4 mr-1"/>Confermato</span>
        )}
      </div>
      {pid && (data.barcode_number || "").trim() && (
        <BarcodeDownload pid={pid} number={data.barcode_number}/>
      )}
    </div>
  );
}

function BarcodeDownload({ pid, number }) {
  const [busy, setBusy] = useState(null); // "png" | "svg" | "pdf"
  const [previewUrl, setPreviewUrl] = useState(null);
  // The generate endpoint requires auth; fetch the PNG once (per number/pid)
  // and expose it as a blob URL so the <img> tag can render without needing
  // to send Authorization headers.
  useEffect(() => {
    let cancelled = false;
    let blobUrl = null;
    (async () => {
      try {
        const token = getActiveToken();
        const res = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/products/${pid}/barcode/generate?format=png`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!res.ok) return;
        const b = await res.blob();
        blobUrl = URL.createObjectURL(b);
        if (!cancelled) setPreviewUrl(blobUrl);
      } catch { /* silent */ }
    })();
    return () => {
      cancelled = true;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [pid, number]);
  const dl = async (fmt) => {
    setBusy(fmt);
    try {
      const token = getActiveToken();
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/products/${pid}/barcode/generate?format=${fmt}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) {
        const err = await res.json().catch(() => null);
        throw new Error(err?.detail || "download failed");
      }
      const b = await res.blob();
      const cd = res.headers.get("content-disposition") || "";
      const m = /filename="?([^"]+)"?/i.exec(cd);
      const fname = m ? m[1] : `barcode.${fmt}`;
      const a = document.createElement("a");
      a.href = URL.createObjectURL(b);
      a.download = fname;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (err) {
      toast.error(err?.message || "Errore download barcode");
    } finally { setBusy(null); }
  };
  return (
    <div className="border-t border-[#E5E5E0] pt-6" data-testid="barcode-download-section">
      <div className="label-tiny mb-3">Codice a barre generato</div>
      <div className="flex flex-col sm:flex-row items-start gap-6">
        <div className="bg-white border border-[#E5E5E0] p-4 flex items-center justify-center min-h-[80px]">
          {previewUrl ? (
            <img
              src={previewUrl}
              alt={`Barcode ${number}`}
              className="max-w-[240px] h-auto"
              data-testid="barcode-preview-img"
            />
          ) : (
            <span className="text-xs text-[#707070]">Generazione anteprima...</span>
          )}
        </div>
        <div className="space-y-3">
          <div className="text-xs text-[#707070]">
            Scarica il codice a barre generato dal numero <strong className="font-mono">{number}</strong>
            {number && (/^\d{12,13}$/.test(number.trim()) ? " (EAN-13)" : " (Code 128)")}
          </div>
          <div className="flex flex-wrap gap-2">
            <button onClick={() => dl("png")} disabled={busy === "png"} className="btn-ghost flex items-center"
                    data-testid="barcode-download-png-btn">
              <Download className="w-3 h-3 mr-2" strokeWidth={1.5}/>PNG{busy === "png" ? "..." : ""}
            </button>
            <button onClick={() => dl("svg")} disabled={busy === "svg"} className="btn-ghost flex items-center"
                    data-testid="barcode-download-svg-btn">
              <Download className="w-3 h-3 mr-2" strokeWidth={1.5}/>SVG{busy === "svg" ? "..." : ""}
            </button>
            <button onClick={() => dl("pdf")} disabled={busy === "pdf"} className="btn-ghost flex items-center"
                    data-testid="barcode-download-pdf-btn">
              <Download className="w-3 h-3 mr-2" strokeWidth={1.5}/>PDF{busy === "pdf" ? "..." : ""}
            </button>
            <button onClick={() => dl("eps")} disabled={busy === "eps"} className="btn-ghost flex items-center"
                    data-testid="barcode-download-eps-btn">
              <Download className="w-3 h-3 mr-2" strokeWidth={1.5}/>EPS{busy === "eps" ? "..." : ""}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Logistica({ data, setData, pid, isNew, ensureSaved, reload }) {
  const set = (k, v) => setData(d => ({ ...d, [k]: v }));
  return (
    <div className="space-y-10" data-testid="logistica-tab">
      <div>
        <div className="label-tiny mb-4">Peso & Dimensioni</div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div>
            <label className="label-tiny">Peso (g)</label>
            <input className="input-luxury" type="number" data-testid="input-weight"
                   value={data.weight_g ?? ""}
                   onChange={(e)=>set("weight_g", e.target.value)} />
          </div>
          <div>
            <label className="label-tiny">Lunghezza (cm)</label>
            <input className="input-luxury" type="number" step="0.1" data-testid="input-length"
                   value={data.length_cm ?? ""}
                   onChange={(e)=>set("length_cm", e.target.value)} />
          </div>
          <div>
            <label className="label-tiny">Larghezza (cm)</label>
            <input className="input-luxury" type="number" step="0.1" data-testid="input-width"
                   value={data.width_cm ?? ""}
                   onChange={(e)=>set("width_cm", e.target.value)} />
          </div>
          <div>
            <label className="label-tiny">Altezza (cm)</label>
            <input className="input-luxury" type="number" step="0.1" data-testid="input-height"
                   value={data.height_cm ?? ""}
                   onChange={(e)=>set("height_cm", e.target.value)} />
          </div>
          <div className="md:col-span-4 text-xs text-[#707070]">Se vuoti, ereditano dalla sottocategoria. Non visibili nel QR.</div>
        </div>
      </div>
    </div>
  );
}

function SafetySheetSection({ pid, isNew, ensureSaved, currentFileId, onChange, reload }) {
  const [versions, setVersions] = useState([]);
  const [versionDate, setVersionDate] = useState("");
  const [busy, setBusy] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  const [dateModalOpen, setDateModalOpen] = useState(false);
  const [modalDate, setModalDate] = useState("");
  const [previewVersion, setPreviewVersion] = useState(null);
  const fileRef = useRef();

  useEffect(() => {
    if (!pid) { setVersions([]); return; }
    api.get(`/products/${pid}/safety-sheet/versions`).then(r => setVersions(r.data)).catch(() => {});
  }, [pid, currentFileId]);

  // Always let the user pick the file first — the mandatory date is requested
  // AFTER selection (via a modal) because the emission date is usually found
  // inside the document itself.
  const requestUpload = () => {
    if (!busy) fileRef.current?.click();
  };

  const doUpload = async (file, dateLabel) => {
    if (!file || !dateLabel) return;
    const productId = pid || (ensureSaved && await ensureSaved());
    if (!productId) { if (fileRef.current) fileRef.current.value = ""; return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("version_label", dateLabel);
      const r = await api.post(`/products/${productId}/safety-sheet`, fd);
      toast.success("Scheda sicurezza caricata");
      onChange?.(r.data.file_id);
      setVersionDate("");
      if (reload) reload(productId);
      const v = await api.get(`/products/${productId}/safety-sheet/versions`);
      setVersions(v.data);
    } catch (err) { toast.error(err.response?.data?.detail || "Errore upload"); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const onFileSelected = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!checkFileSize(f)) { if (fileRef.current) fileRef.current.value = ""; return; }
    setPendingFile(f);
    // Free the input so re-selecting the same file later still fires onChange.
    if (fileRef.current) fileRef.current.value = "";
    if (versionDate) {
      doUpload(f, versionDate);
    } else {
      setModalDate("");
      setDateModalOpen(true);
    }
  };

  const confirmDateAndUpload = () => {
    if (!modalDate) return;
    setVersionDate(modalDate);
    setDateModalOpen(false);
    const f = pendingFile;
    setPendingFile(null);
    doUpload(f, modalDate);
  };

  const cancelDateModal = () => {
    setDateModalOpen(false);
    setPendingFile(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  const removeVersion = async (verId) => {
    if (!confirm("Eliminare questa versione?")) return;
    try {
      await api.delete(`/products/${pid}/safety-sheet/versions/${verId}`);
      const v = await api.get(`/products/${pid}/safety-sheet/versions`);
      setVersions(v.data);
      if (reload) reload(pid);
    } catch { toast.error("Errore"); }
  };

  const apiBase = process.env.REACT_APP_BACKEND_URL;
  const isCurrent = (fid) => fid === currentFileId;

  const fmtDate = (s) => {
    if (!s) return "";
    const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
    return m ? `${m[3]}/${m[2]}/${m[1]}` : s;
  };

  return (
    <div data-testid="safety-sheet-section">
      <div className="mb-4">
        <div className="label-tiny mb-0">Scheda di sicurezza (SDS)</div>
        <div className="text-xs text-[#707070] mt-1 max-w-xl mb-4">
          La versione più recente sarà disponibile nel QR pubblico. Le precedenti restano archiviate.
          Seleziona il file: ti verrà chiesta la <strong>data di emissione</strong> del documento (obbligatoria) prima di completare il caricamento.
        </div>
        <div>
          <div className="flex items-end gap-3 flex-wrap">
            <div>
              <label className="label-tiny" htmlFor="safety-version-date">
                Versione (data documento) <span className="text-[#8C3A3A]">*</span>
              </label>
              <input id="safety-version-date" type="date"
                     value={versionDate}
                     onChange={(e)=>setVersionDate(e.target.value)}
                     className="input-luxury text-sm py-1.5"
                     data-testid="safety-version-date" />
            </div>
            <input ref={fileRef} type="file" accept=".pdf,.doc,.docx"
                   onChange={onFileSelected} className="hidden" data-testid="safety-input" />
            <button onClick={requestUpload}
                    disabled={busy}
                    className={`btn-primary flex items-center ${busy ? "opacity-40 cursor-not-allowed" : ""}`}
                    data-testid="safety-upload-btn">
              <Upload className="w-3 h-3 mr-2" strokeWidth={1.5}/>
              {busy ? "Caricamento..." : "Carica nuova versione"}
            </button>
          </div>
          <UploadLimitNote className="mt-1" />
          <div className="text-[11px] text-[#707070] mt-1">
            Facoltativa qui: se vuota, la chiederemo dopo aver scelto il file.
          </div>
        </div>
      </div>
      {versions.length === 0 ? (
        <div className="luxury-card p-6 text-center text-sm text-[#707070]">
          Nessuna scheda di sicurezza caricata.
        </div>
      ) : (
        <div className="luxury-card divide-y divide-[#E5E5E0]">
          {versions.map(v => (
            <div key={v.id} className="flex items-center justify-between px-4 py-3"
                 data-testid={`safety-version-${v.id}`}>
              <div className="flex items-center gap-3 min-w-0">
                <div className={`w-2 h-2 rounded-full ${isCurrent(v.file_id) ? "bg-[#4A6B53]" : "bg-[#E5E5E0]"}`}/>
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate flex items-center gap-2">
                    {fmtDate(v.version_label) || v.version_label || <em className="text-[#A0A0A0]">Senza versione</em>}
                    {isCurrent(v.file_id) && (
                      <span className="text-[10px] tracking-luxury uppercase text-[#4A6B53]">Corrente</span>
                    )}
                  </div>
                  <div className="text-xs text-[#707070]">
                    Caricata il {(v.version_date || "").slice(0,10)} · {v.original_filename || "scheda.pdf"}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={()=>setPreviewVersion(v)}
                        className="text-xs text-[#1A1A1A] hover:text-[#707070] flex items-center"
                        title="Anteprima"
                        data-testid={`safety-preview-${v.id}`}>
                  <Eye className="w-3 h-3 mr-1" strokeWidth={1.5}/>Anteprima
                </button>
                <a href={`${apiBase}/api/files/${v.file_id}`} target="_blank" rel="noreferrer"
                   className="text-xs text-[#1A1A1A] hover:text-[#707070] flex items-center ml-2"
                   data-testid={`safety-download-${v.id}`}>
                  <Download className="w-3 h-3 mr-1" strokeWidth={1.5}/>Scarica
                </a>
                <button onClick={()=>removeVersion(v.id)}
                        className="text-xs text-[#8C3A3A] hover:opacity-70 flex items-center ml-2"
                        title="Elimina versione"
                        data-testid={`safety-remove-${v.id}`}>
                  <Trash2 className="w-3 h-3" strokeWidth={1.5}/>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {dateModalOpen && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center px-4"
             onClick={cancelDateModal} data-testid="safety-date-modal">
          <div className="bg-white rounded-sm shadow-xl w-full max-w-md p-6" onClick={(e)=>e.stopPropagation()}>
            <div className="flex items-start justify-between mb-3">
              <h3 className="font-serif text-2xl">Data del documento richiesta</h3>
              <button onClick={cancelDateModal} className="text-[#707070] hover:text-[#1A1A1A]"
                      data-testid="safety-date-modal-close"><X className="w-4 h-4"/></button>
            </div>
            <p className="text-sm text-[#707070] mb-2">
              Il file <strong>{pendingFile?.name || "selezionato"}</strong> è pronto, ma la scheda di sicurezza
              <strong> non può essere salvata senza la data di emissione</strong> del documento. Inseriscila per completare il caricamento.
            </p>
            <label className="label-tiny mb-1 block" htmlFor="safety-modal-date">
              Data di emissione <span className="text-[#8C3A3A]">*</span>
            </label>
            <input id="safety-modal-date" type="date" autoFocus
                   value={modalDate}
                   onChange={(e)=>setModalDate(e.target.value)}
                   className="input-luxury text-sm w-full"
                   data-testid="safety-modal-date-input" />
            <div className="flex justify-end gap-3 mt-5">
              <button onClick={cancelDateModal} className="btn-ghost"
                      data-testid="safety-date-modal-cancel">Annulla</button>
              <button onClick={confirmDateAndUpload} disabled={!modalDate || busy}
                      className={`btn-primary flex items-center ${!modalDate || busy ? "opacity-40 cursor-not-allowed" : ""}`}
                      data-testid="safety-date-modal-confirm">
                <Upload className="w-3 h-3 mr-2" strokeWidth={2}/>{busy ? "Caricamento..." : "Conferma e carica"}
              </button>
            </div>
          </div>
        </div>
      )}

      {previewVersion && (
        <LabelPreviewModal
          label={{
            url: `/api/files/${previewVersion.file_id}`,
            mime_type: previewVersion.mime_type || "application/pdf",
            original_filename: previewVersion.original_filename || "Scheda di sicurezza",
          }}
          onClose={()=>setPreviewVersion(null)} />
      )}
    </div>
  );
}

function DocumentiTecnici({ pid, isNew, ensureSaved, data, setData, reload, packagingCount, onPackagingCountChange }) {
  const [sub, setSub] = useState("sds");
  const items = [
    { key: "sds", label: "SDS", sub: "Scheda di sicurezza", icon: ShieldCheck,
      done: !!data.safety_sheet_current_file_id },
    { key: "cpnp", label: "Notifica CPNP", sub: "Registrazione portale", icon: ClipboardCheck,
      done: !!data.cpnp_notification_file_id },
    { key: "pif", label: "PIF", sub: "Product Information File", icon: FolderOpen,
      done: !!data.pif_current_file_id },
    { key: "packaging", label: "Certificazione", sub: "Documenti PDF", icon: Package,
      done: packagingCount > 0 },
  ];
  return (
    <div data-testid="sicurezza-tab">
      <div className="text-xs text-[#707070] mb-4 flex items-center gap-2">
        <ShieldCheck className="w-3.5 h-3.5 text-[#C5B39B]" strokeWidth={1.5}/>
        Documenti tecnici del prodotto. La Certificazione Packaging può essere resa visibile dal Layout QR.
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-8" data-testid="doctech-subtabs">
        {items.map(it => {
          const Icon = it.icon;
          const active = sub === it.key;
          return (
            <button key={it.key} onClick={()=>setSub(it.key)} type="button"
                    data-testid={`doctech-tab-${it.key}`}
                    className={`text-left p-4 border rounded-sm transition group relative
                      ${active ? "border-[#1A1A1A] bg-[#1A1A1A] text-white shadow-sm"
                               : "border-[#E5E5E0] bg-white hover:border-[#C5B39B]"}`}>
              <div className="flex items-center justify-between mb-2">
                <Icon className={`w-5 h-5 ${active ? "text-[#C5B39B]" : "text-[#707070] group-hover:text-[#1A1A1A]"}`} strokeWidth={1.5}/>
                <ProductStatus tone={it.done ? "success" : "warning"}
                               icon={it.done ? CheckCircle2 : AlertTriangle}
                               testId={it.key === "packaging" ? "doctech-packaging-status" : `doctech-${it.key}-status`}>
                  {it.done ? "Caricato" : "Mancante"}
                </ProductStatus>
              </div>
              <div className={`font-serif text-lg leading-tight ${active ? "text-white" : "text-[#1A1A1A]"}`}>{it.label}</div>
              <div className={`text-xs mt-0.5 ${active ? "text-white/60" : "text-[#707070]"}`}>{it.sub}</div>
            </button>
          );
        })}
      </div>

      <div className="luxury-card p-5 md:p-7" data-testid="doctech-panel">
        {sub === "sds" && (
          <SafetySheetSection pid={pid} isNew={isNew} ensureSaved={ensureSaved}
                               currentFileId={data.safety_sheet_current_file_id}
                               onChange={(fid) => setData(d => ({ ...d, safety_sheet_current_file_id: fid }))}
                               reload={reload} />
        )}
        {sub === "cpnp" && (
          <CpnpNotificationSection pid={pid} isNew={isNew} ensureSaved={ensureSaved}
                                    data={data} setData={setData} reload={reload} />
        )}
        {sub === "pif" && (
          <PifSection pid={pid} isNew={isNew} ensureSaved={ensureSaved}
                      currentFileId={data.pif_current_file_id}
                      onChange={(fid) => setData(d => ({ ...d, pif_current_file_id: fid }))}
                      reload={reload} />
        )}
        {sub === "packaging" && (
          <PackagingCertificationSection
            pid={pid} ensureSaved={ensureSaved}
            onCountChange={onPackagingCountChange}
          />
        )}
      </div>
    </div>
  );
}


function CpnpNotificationSection({ pid, isNew, ensureSaved, data, setData, reload }) {
  const [versions, setVersions] = useState([]);
  const [busy, setBusy] = useState(false);
  const [previewVersion, setPreviewVersion] = useState(null);
  const fileRef = useRef();
  const set = (k, v) => setData(d => ({ ...d, [k]: v }));

  useEffect(() => {
    if (!pid) { setVersions([]); return; }
    api.get(`/products/${pid}/cpnp-notification/versions`).then(r => setVersions(r.data)).catch(() => {});
  }, [pid, data.cpnp_notification_file_id]);

  const requestUpload = () => { if (!busy) fileRef.current?.click(); };

  const onFileSelected = async (e) => {
    const f = e.target.files?.[0];
    if (fileRef.current) fileRef.current.value = "";
    if (!f) return;
    if (!checkFileSize(f)) return;
    const productId = pid || (ensureSaved && await ensureSaved());
    if (!productId) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post(`/products/${productId}/cpnp-notification`, fd);
      const ex = r.data.extracted || {};
      setData(d => ({ ...d,
        cpnp_notification_file_id: r.data.version?.file_id || d.cpnp_notification_file_id,
        cpnp_reference: ex.reference || "",
        cpnp_version: ex.version || "",
        cpnp_first_notification_date: ex.first_notification_date || "",
        cpnp_last_modification_date: ex.last_modification_date || "",
      }));
      toast.success("Notifica CPNP caricata — dati estratti dall'AI");
      const v = await api.get(`/products/${productId}/cpnp-notification/versions`);
      setVersions(v.data);
      if (reload) reload(productId);
    } catch (err) { toast.error(err.response?.data?.detail || "Errore upload CPNP"); }
    finally { setBusy(false); }
  };

  const removeVersion = async (verId) => {
    if (!confirm("Eliminare questo documento CPNP?")) return;
    try {
      await api.delete(`/products/${pid}/cpnp-notification/versions/${verId}`);
      const v = await api.get(`/products/${pid}/cpnp-notification/versions`);
      setVersions(v.data);
      if (reload) reload(pid);
    } catch { toast.error("Errore"); }
  };

  const apiBase = process.env.REACT_APP_BACKEND_URL;

  return (
    <div data-testid="cpnp-section">
      <div className="text-xs text-[#707070] mb-4 max-w-xl">
        Carica il PDF della notifica CPNP: l'AI analizza l'intestazione della prima pagina e compila
        automaticamente i campi sottostanti. Puoi correggerli a mano; salva con il pulsante <strong>Salva</strong> in alto.
        Questi dati restano <strong>privati</strong> (non compaiono nel catalogo pubblico né nel QR).
      </div>
      <div className="mb-6">
        <input ref={fileRef} type="file" accept=".pdf" onChange={onFileSelected} className="hidden" data-testid="cpnp-input" />
        <button onClick={requestUpload} disabled={busy}
                className={`btn-primary inline-flex items-center ${busy ? "opacity-40 cursor-not-allowed" : ""}`}
                data-testid="cpnp-upload-btn">
          {busy ? <Loader2 className="w-3 h-3 mr-2 animate-spin"/> : <Upload className="w-3 h-3 mr-2" strokeWidth={1.5}/>}
          {busy ? "Analisi AI in corso..." : "Carica notifica CPNP (PDF)"}
        </button>
        <UploadLimitNote className="mt-2" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div>
          <label className="label-tiny">Riferimento CPNP</label>
          <input className="input-luxury" data-testid="cpnp-reference"
                 value={data.cpnp_reference || ""} placeholder="es. 1234567"
                 onChange={(e)=>set("cpnp_reference", e.target.value)} />
        </div>
        <div>
          <label className="label-tiny">Versione</label>
          <input className="input-luxury" data-testid="cpnp-version"
                 value={data.cpnp_version || ""} placeholder="es. 1"
                 onChange={(e)=>set("cpnp_version", e.target.value)} />
        </div>
        <div>
          <label className="label-tiny">Data della prima notifica</label>
          <input type="date" className="input-luxury" data-testid="cpnp-first-date"
                 value={data.cpnp_first_notification_date || ""}
                 onChange={(e)=>set("cpnp_first_notification_date", e.target.value)} />
        </div>
        <div>
          <label className="label-tiny">Data dell'ultima modifica</label>
          <input type="date" className="input-luxury" data-testid="cpnp-last-date"
                 value={data.cpnp_last_modification_date || ""}
                 onChange={(e)=>set("cpnp_last_modification_date", e.target.value)} />
        </div>
      </div>

      {versions.length > 0 && (
        <div className="luxury-card divide-y divide-[#E5E5E0] mt-6">
          {versions.map(v => (
            <div key={v.id} className="flex items-center justify-between px-4 py-3" data-testid={`cpnp-version-${v.id}`}>
              <div className="flex items-center gap-3 min-w-0">
                <FileText className="w-4 h-4 text-[#707070]" strokeWidth={1.5}/>
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">{v.original_filename || "cpnp.pdf"}</div>
                  <div className="text-xs text-[#707070]">Caricato il {(v.created_at || "").slice(0,10)}</div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={()=>setPreviewVersion(v)} className="text-xs text-[#1A1A1A] hover:text-[#707070] flex items-center"
                        data-testid={`cpnp-preview-${v.id}`}>
                  <Eye className="w-3 h-3 mr-1" strokeWidth={1.5}/>Anteprima
                </button>
                <a href={`${apiBase}/api/files/${v.file_id}`} target="_blank" rel="noreferrer"
                   className="text-xs text-[#1A1A1A] hover:text-[#707070] flex items-center ml-2"
                   data-testid={`cpnp-download-${v.id}`}>
                  <Download className="w-3 h-3 mr-1" strokeWidth={1.5}/>Scarica
                </a>
                <button onClick={()=>removeVersion(v.id)} className="text-xs text-[#8C3A3A] hover:opacity-70 flex items-center ml-2"
                        data-testid={`cpnp-remove-${v.id}`}>
                  <Trash2 className="w-3 h-3" strokeWidth={1.5}/>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {previewVersion && (
        <LabelPreviewModal
          label={{
            url: `/api/files/${previewVersion.file_id}`,
            mime_type: previewVersion.mime_type || "application/pdf",
            original_filename: previewVersion.original_filename || "Notifica CPNP",
          }}
          onClose={()=>setPreviewVersion(null)} />
      )}
    </div>
  );
}

function PifSection({ pid, isNew, ensureSaved, currentFileId, onChange, reload }) {
  const [versions, setVersions] = useState([]);
  const [versionDate, setVersionDate] = useState("");
  const [busy, setBusy] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  const [dateModalOpen, setDateModalOpen] = useState(false);
  const [modalDate, setModalDate] = useState("");
  const [previewVersion, setPreviewVersion] = useState(null);
  const fileRef = useRef();

  useEffect(() => {
    if (!pid) { setVersions([]); return; }
    api.get(`/products/${pid}/pif/versions`).then(r => setVersions(r.data)).catch(() => {});
  }, [pid, currentFileId]);

  const requestUpload = () => { if (!busy) fileRef.current?.click(); };

  const doUpload = async (file, dateLabel) => {
    if (!file || !dateLabel) return;
    const productId = pid || (ensureSaved && await ensureSaved());
    if (!productId) { if (fileRef.current) fileRef.current.value = ""; return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("version_label", dateLabel);
      const r = await api.post(`/products/${productId}/pif`, fd);
      toast.success("PIF caricato");
      onChange?.(r.data.file_id);
      setVersionDate("");
      if (reload) reload(productId);
      const v = await api.get(`/products/${productId}/pif/versions`);
      setVersions(v.data);
    } catch (err) { toast.error(err.response?.data?.detail || "Errore upload"); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const onFileSelected = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!checkFileSize(f)) { if (fileRef.current) fileRef.current.value = ""; return; }
    setPendingFile(f);
    if (fileRef.current) fileRef.current.value = "";
    if (versionDate) doUpload(f, versionDate);
    else { setModalDate(""); setDateModalOpen(true); }
  };

  const confirmDateAndUpload = () => {
    if (!modalDate) return;
    setVersionDate(modalDate);
    setDateModalOpen(false);
    const f = pendingFile;
    setPendingFile(null);
    doUpload(f, modalDate);
  };

  const cancelDateModal = () => {
    setDateModalOpen(false);
    setPendingFile(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  const removeVersion = async (verId) => {
    if (!confirm("Eliminare questa versione?")) return;
    try {
      await api.delete(`/products/${pid}/pif/versions/${verId}`);
      const v = await api.get(`/products/${pid}/pif/versions`);
      setVersions(v.data);
      if (reload) reload(pid);
    } catch { toast.error("Errore"); }
  };

  const apiBase = process.env.REACT_APP_BACKEND_URL;
  const isCurrent = (fid) => fid === currentFileId;
  const fmtDate = (s) => {
    if (!s) return "";
    const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
    return m ? `${m[3]}/${m[2]}/${m[1]}` : s;
  };

  return (
    <div data-testid="pif-section">
      <div className="text-xs text-[#707070] mb-4 max-w-xl">
        Carica il PDF del PIF e imposta una <strong>data di riferimento</strong>. Documento privato,
        non pubblicato nel catalogo/QR.
      </div>
      <div className="flex items-end gap-3 flex-wrap mb-2">
        <div>
          <label className="label-tiny" htmlFor="pif-version-date">
            Data di riferimento <span className="text-[#8C3A3A]">*</span>
          </label>
          <input id="pif-version-date" type="date" value={versionDate}
                 onChange={(e)=>setVersionDate(e.target.value)}
                 className="input-luxury text-sm py-1.5" data-testid="pif-version-date" />
        </div>
        <input ref={fileRef} type="file" accept=".pdf,.doc,.docx"
               onChange={onFileSelected} className="hidden" data-testid="pif-input" />
        <button onClick={requestUpload} disabled={busy}
                className={`btn-primary flex items-center ${busy ? "opacity-40 cursor-not-allowed" : ""}`}
                data-testid="pif-upload-btn">
          <Upload className="w-3 h-3 mr-2" strokeWidth={1.5}/>
          {busy ? "Caricamento..." : "Carica PIF"}
        </button>
      </div>
      <UploadLimitNote className="mb-2" />
      <div className="text-[11px] text-[#707070] mb-4">
        Facoltativa qui: se vuota, la chiederemo dopo aver scelto il file.
      </div>

      {versions.length === 0 ? (
        <div className="luxury-card p-6 text-center text-sm text-[#707070]">Nessun PIF caricato.</div>
      ) : (
        <div className="luxury-card divide-y divide-[#E5E5E0]">
          {versions.map(v => (
            <div key={v.id} className="flex items-center justify-between px-4 py-3" data-testid={`pif-version-${v.id}`}>
              <div className="flex items-center gap-3 min-w-0">
                <div className={`w-2 h-2 rounded-full ${isCurrent(v.file_id) ? "bg-[#4A6B53]" : "bg-[#E5E5E0]"}`}/>
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate flex items-center gap-2">
                    {fmtDate(v.version_label) || v.version_label || <em className="text-[#A0A0A0]">Senza data</em>}
                    {isCurrent(v.file_id) && <span className="text-[10px] tracking-luxury uppercase text-[#4A6B53]">Corrente</span>}
                  </div>
                  <div className="text-xs text-[#707070]">
                    Caricato il {(v.version_date || "").slice(0,10)} · {v.original_filename || "pif.pdf"}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={()=>setPreviewVersion(v)} className="text-xs text-[#1A1A1A] hover:text-[#707070] flex items-center"
                        data-testid={`pif-preview-${v.id}`}>
                  <Eye className="w-3 h-3 mr-1" strokeWidth={1.5}/>Anteprima
                </button>
                <a href={`${apiBase}/api/files/${v.file_id}`} target="_blank" rel="noreferrer"
                   className="text-xs text-[#1A1A1A] hover:text-[#707070] flex items-center ml-2"
                   data-testid={`pif-download-${v.id}`}>
                  <Download className="w-3 h-3 mr-1" strokeWidth={1.5}/>Scarica
                </a>
                <button onClick={()=>removeVersion(v.id)} className="text-xs text-[#8C3A3A] hover:opacity-70 flex items-center ml-2"
                        data-testid={`pif-remove-${v.id}`}>
                  <Trash2 className="w-3 h-3" strokeWidth={1.5}/>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {dateModalOpen && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center px-4"
             onClick={cancelDateModal} data-testid="pif-date-modal">
          <div className="bg-white rounded-sm shadow-xl w-full max-w-md p-6" onClick={(e)=>e.stopPropagation()}>
            <div className="flex items-start justify-between mb-3">
              <h3 className="font-serif text-2xl">Data di riferimento richiesta</h3>
              <button onClick={cancelDateModal} className="text-[#707070] hover:text-[#1A1A1A]"
                      data-testid="pif-date-modal-close"><X className="w-4 h-4"/></button>
            </div>
            <p className="text-sm text-[#707070] mb-2">
              Il file <strong>{pendingFile?.name || "selezionato"}</strong> è pronto. Inserisci la
              <strong> data di riferimento</strong> per completare il caricamento.
            </p>
            <label className="label-tiny mb-1 block" htmlFor="pif-modal-date">
              Data di riferimento <span className="text-[#8C3A3A]">*</span>
            </label>
            <input id="pif-modal-date" type="date" autoFocus value={modalDate}
                   onChange={(e)=>setModalDate(e.target.value)}
                   className="input-luxury text-sm w-full" data-testid="pif-modal-date-input" />
            <div className="flex justify-end gap-3 mt-5">
              <button onClick={cancelDateModal} className="btn-ghost" data-testid="pif-date-modal-cancel">Annulla</button>
              <button onClick={confirmDateAndUpload} disabled={!modalDate || busy}
                      className={`btn-primary flex items-center ${!modalDate || busy ? "opacity-40 cursor-not-allowed" : ""}`}
                      data-testid="pif-date-modal-confirm">
                <Upload className="w-3 h-3 mr-2" strokeWidth={2}/>{busy ? "Caricamento..." : "Conferma e carica"}
              </button>
            </div>
          </div>
        </div>
      )}

      {previewVersion && (
        <LabelPreviewModal
          label={{
            url: `/api/files/${previewVersion.file_id}`,
            mime_type: previewVersion.mime_type || "application/pdf",
            original_filename: previewVersion.original_filename || "PIF",
          }}
          onClose={()=>setPreviewVersion(null)} />
      )}
    </div>
  );
}


function QrTab({ pid, isNew, publicUrl, productQr, data, setData }) {
  const { subscriptionDisabled } = useAuth();
  if (subscriptionDisabled) return <p className="text-sm text-[#8C3A3A]" data-testid="qr-subscription-disabled">I link QR sono temporaneamente disattivati.</p>;
  if (isNew) return <div className="text-sm text-[#707070]">Salva prima il prodotto per generare il QR.</div>;
  const dl = async (fmt) => {
    const token = getActiveToken();
    const res = await fetch(
      `${process.env.REACT_APP_BACKEND_URL}/api/products/${pid}/qr/download?format=${fmt}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    const b = await res.blob();
    // Backend sets Content-Disposition: attachment; filename="{code}-{name}.{ext}"
    // Parse it so the browser saves with the friendly name.
    const cd = res.headers.get("content-disposition") || "";
    const m = /filename="?([^"]+)"?/i.exec(cd);
    const fname = m ? m[1] : `qr.${fmt}`;
    const a = document.createElement("a");
    a.href = URL.createObjectURL(b);
    a.download = fname;
    a.click();
    URL.revokeObjectURL(a.href);
  };
  const regen = async () => {
    await api.post(`/products/${pid}/qr/regenerate`);
    toast.success("QR rigenerato");
  };
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(publicUrl);
      toast.success("Link copiato");
    } catch {
      toast.error("Impossibile copiare il link");
    }
  };
  return (
    <div className="space-y-6" data-testid="qr-tab">
      <div>
        <div className="label-tiny mb-2">URL pubblico</div>
        <div className="flex gap-3 items-center">
          <code className="text-sm bg-[#F4F4F0] px-4 py-2 rounded-sm flex-1 break-all">{publicUrl}</code>
          <button onClick={copyLink} className="btn-ghost" data-testid="qr-copy-btn">Copia</button>
          <a href={publicUrl} target="_blank" rel="noreferrer" className="btn-ghost" data-testid="qr-preview-btn">Anteprima</a>
        </div>
      </div>
      {productQr.png && (
        <div className="flex items-start gap-8">
          <img src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${productQr.png}`}
               className="w-56 h-56 border border-[#E5E5E0]" alt="QR Code" data-testid="qr-image" />
          <div className="flex flex-col gap-3">
            <button onClick={()=>dl("png")} className="btn-ghost flex items-center" data-testid="qr-download-png">
              <Download className="w-3 h-3 mr-2" />Scarica PNG
            </button>
            <button onClick={()=>dl("svg")} className="btn-ghost flex items-center" data-testid="qr-download-svg">
              <Download className="w-3 h-3 mr-2" />Scarica SVG
            </button>
            <button onClick={()=>dl("pdf")} className="btn-ghost flex items-center" data-testid="qr-download-pdf">
              <Download className="w-3 h-3 mr-2" />Scarica PDF
            </button>
            <button onClick={regen} className="text-xs text-[#707070] underline mt-2" data-testid="qr-regen-btn">Rigenera</button>
          </div>
        </div>
      )}
    </div>
  );
}


/**
 * Searchable country selector. Click opens a popover with a text-search
 * input that filters by name OR alpha-2 code (case- and accent-insensitive
 * prefix match first, then substring match). Keyboard: arrows + Enter to
 * select, Escape to close.
 */
function CountryPicker({ value, options, onChange, placeholder = "—", testId }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [highlight, setHighlight] = useState(0);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  const selected = useMemo(
    () => options.find(o => o.code === value) || null,
    [options, value]
  );

  const norm = (s) => (s || "")
    .toString()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

  const filtered = useMemo(() => {
    const q = norm(query.trim());
    if (!q) return options;
    const starts = [], contains = [];
    for (const o of options) {
      const nName = norm(o.name);
      const nCode = norm(o.code);
      if (nName.startsWith(q) || nCode.startsWith(q)) starts.push(o);
      else if (nName.includes(q)) contains.push(o);
    }
    return [...starts, ...contains];
  }, [options, query]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  // Focus search input + reset highlight when opening
  useEffect(() => {
    if (open) {
      setQuery("");
      setHighlight(0);
      setTimeout(() => inputRef.current?.focus(), 30);
    }
  }, [open]);

  // Keep highlighted item in view
  useEffect(() => {
    if (!open) return;
    const li = listRef.current?.querySelector(`[data-idx="${highlight}"]`);
    li?.scrollIntoView({ block: "nearest" });
  }, [highlight, open]);

  const pick = (code) => {
    onChange(code);
    setOpen(false);
  };

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setHighlight(h => Math.min(h + 1, filtered.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHighlight(h => Math.max(h - 1, 0)); }
    else if (e.key === "Enter") { e.preventDefault(); if (filtered[highlight]) pick(filtered[highlight].code); }
    else if (e.key === "Escape") { setOpen(false); }
  };

  return (
    <div ref={wrapRef} className="relative" data-testid={`${testId}-wrap`}>
      <button type="button"
              onClick={() => setOpen(o => !o)}
              className="input-luxury w-full flex items-center justify-between text-left"
              data-testid={testId}
              data-value={value || ""}>
        <span className={selected ? "text-[#121212]" : "product-ux-empty-text"}>
          {selected ? selected.name : placeholder}
        </span>
        <ChevronDown className="w-4 h-4 text-[#707070] ml-2 shrink-0" strokeWidth={1.5} />
      </button>
      {open && (
        <div className="absolute z-50 mt-1 left-0 right-0 bg-white border border-[#E5E5E0] shadow-lg rounded-sm"
             data-testid={`${testId}-popover`}>
          <div className="flex items-center gap-2 px-3 py-2 border-b border-[#E5E5E0]">
            <Search className="w-4 h-4 text-[#707070]" strokeWidth={1.5} />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => { setQuery(e.target.value); setHighlight(0); }}
              onKeyDown={onKeyDown}
              placeholder="Cerca paese..."
              className="flex-1 text-sm outline-none bg-transparent"
              data-testid={`${testId}-search`}
            />
            {value ? (
              <button type="button"
                      onClick={(e) => { e.stopPropagation(); pick(""); }}
                      className="text-[10px] tracking-luxury uppercase text-[#707070] hover:text-[#1A1A1A]"
                      data-testid={`${testId}-clear`}>
                Pulisci
              </button>
            ) : null}
          </div>
          <ul ref={listRef}
              className="max-h-64 overflow-auto py-1"
              data-testid={`${testId}-options`}>
            {filtered.length === 0 ? (
              <li className="px-3 py-2 text-sm text-[#A0A0A0] italic">Nessun paese trovato</li>
            ) : filtered.map((o, idx) => (
              <li key={o.code}
                  data-idx={idx}
                  onMouseEnter={() => setHighlight(idx)}
                  onClick={() => pick(o.code)}
                  className={`px-3 py-2 text-sm cursor-pointer flex items-center justify-between ${idx === highlight ? "bg-[#FAF9F6]" : ""} ${o.code === value ? "font-medium" : ""}`}
                  data-testid={`${testId}-option-${o.code}`}>
                <span>{o.name}</span>
                <span className="text-[10px] tracking-luxury uppercase text-[#A0A0A0]">{o.code}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
